import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Cards,
  Container,
  ContentLayout,
  Form,
  FormField,
  Header,
  Input,
  Link,
  Select,
  SpaceBetween,
  SplitPanel,
  Box,
  Icon,
  Alert,
  StatusIndicator,
  FileUpload,
  Spinner,
  ColumnLayout,
  HelpPanel,
  ExpandableSection,
  BreadcrumbGroup,
  Wizard,
  Modal,
  Textarea,
} from "@cloudscape-design/components";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { useNotifications } from "./NotificationContext";
import { useFeatureFlags } from "../contexts/FeatureFlagsContext";
import AppLayoutWrapper from "./AppLayoutWrapper";
import "./Chat.css";

// Helper to normalize capabilities that may arrive as a JSON string, array, or object containing a capabilities array
const parseCapabilities = (caps) => {
  if (!caps) return [];
  if (Array.isArray(caps)) return caps;
  if (typeof caps === 'object' && caps !== null) {
    if (Array.isArray(caps.capabilities)) return caps.capabilities;
    return [];
  }
  if (typeof caps === 'string') {
    try {
      const parsed = JSON.parse(caps);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object' && Array.isArray(parsed.capabilities)) return parsed.capabilities;
      return [caps];
    } catch {
      return [caps];
    }
  }
  return [];
};

const LANGUAGE_OPTIONS = [
  { value: "fr", label: "French (Français)" },
  { value: "ar", label: "Arabic (العربية)" },
  { value: "en", label: "English" },
  { value: "es", label: "Spanish (Español)" },
  { value: "it", label: "Italian (Italiano)" },
  { value: "de", label: "German (Deutsch)" },
  { value: "ch", label: "Chinese (中文)" },
  { value: "daridja", label: "Algerian Darija (Daridja)" },
];

const AssistantWizard = () => {
  const { user, refreshToken } = useAuth();
  const { error: notifyError, success: notifySuccess } = useNotifications();
  const { isEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [services, setServices] = useState([]);
  const [agentConfig] = useState({});
  const [isDeploying] = useState(false);
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const [isSplitPanelOpen, setIsSplitPanelOpen] = useState(false);
  const [isSplitPanelSide, setIsSplitPanelSide] = useState("Bottom");
  const [isSplitPanelSize, setIsSplitPanelSize] = useState(400);
  const [agentType, setAgentType] = useState(null);
  const [serviceAgentTypes, setServiceAgentTypes] = useState([]);
  const [serviceAgentType, setServiceAgentType] = useState(null);
  const [loadingServiceAgentTypes, setLoadingServiceAgentTypes] = useState(false);
  const [servicetType, setServiceType] = useState({}); // Update servicetType to hold the complete business type object
  const [fileskn, setFileskn] = useState([]);
  const [stepStatus, setStepStatus] = useState({
    "Pack": "pending",
    "Adexgénie": "pending",
    "Services": "pending",
    "Phone Number": "pending",
    "Files": "pending",
    "Deployability Check": "pending",
  });
  const stepStatusRef = useRef(stepStatus);
  useEffect(() => {
    stepStatusRef.current = stepStatus;
  }, [stepStatus]);
  const [agentName, setAgentName] = useState("");
  const [language, setLanguage] = useState("fr");
  const [gender] = useState("male");
  // Brand info (agent-level)
  const [brandName, setBrandName] = useState("");
  const [brandDescription, setBrandDescription] = useState("");
  const [brandWebsite, setBrandWebsite] = useState("");
  const [businessHours, setBusinessHours] = useState("");
  const [brandContactEmail, setBrandContactEmail] = useState("");
  const [brandContactPhone, setBrandContactPhone] = useState("");
  // New API fields
  const voiceFeatureEnabled = isEnabled("voice");
  const telephonyFeatureEnabled = isEnabled("telephony");
  const voiceAgentAvailable = voiceFeatureEnabled && telephonyFeatureEnabled;
  const [aiAgentType, setAiAgentType] = useState(voiceAgentAvailable ? "voice" : "text");
  useEffect(() => {
    if (!voiceAgentAvailable && aiAgentType === "voice") {
      setAiAgentType("text");
    }
  }, [voiceAgentAvailable, aiAgentType]);
  const [platformMode, setPlatformMode] = useState("dedicated");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [currency, setCurrency] = useState("DZD");
  const [temperature, setTemperature] = useState(0.8);
  const [maxTokens, setMaxTokens] = useState(1024);
  const [twilioProvisionedNumber, setTwilioProvisionedNumber] = useState(null);
  const [agentExternalId, setAgentExternalId] = useState(null);
  const [widgetCode, setWidgetCode] = useState(null);
  const [callStatus, setCallStatus] = useState("idle");
  const [, setLivekitToken] = useState(null);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [isCallLoading, setIsCallLoading] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false); // Loading state for Next button
  const [websocket] = useState(null);
  const [, setAvatarReady] = useState(false);
  const [talkingHead] = useState(null);
  const [, setCallId] = useState(null);
  const [fileStatuses, setFileStatuses] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileidx, setFileidx] = useState([]);
  // On-the-fly resource creation state
  const [wizardWorkflows, setWizardWorkflows] = useState([]);
  const [existingWorkflows, setExistingWorkflows] = useState([]);
  const [selectedExistingWorkflow, setSelectedExistingWorkflow] = useState(null);
  const [loadingExistingWorkflows, setLoadingExistingWorkflows] = useState(false);
  const [wizardKb, setWizardKb] = useState(null);
  const [wizardKbs, setWizardKbs] = useState([]);
  const [existingKbs, setExistingKbs] = useState([]);
  const [selectedExistingKb, setSelectedExistingKb] = useState(null);
  const [loadingExistingKbs, setLoadingExistingKbs] = useState(false);
  const [wizardSipPayload, setWizardSipPayload] = useState({
    trunk_name: '',
    phone_numbers: '',
    allowed_addresses: '',
    allowed_ips: '',
  });
  const [wfModalVisible, setWfModalVisible] = useState(false);
  const [kbModalVisible, setKbModalVisible] = useState(false);
  const [wfForm, setWfForm] = useState({
    name: '',
    description: '',
    workflow_type: 'webhook',
    url: '',
    api_key: '',
    action: '',
    params: [],
  });
  const [kbForm, setKbForm] = useState({
    name: '',
    description: '',
  });
  const [resourceSubmitLoading, setResourceSubmitLoading] = useState(false);

  // Pack selection state
  const [availablePacks, setAvailablePacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [currentPackId, setCurrentPackId] = useState(null);
  const [currentAgentId, setCurrentAgentId] = useState(null);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [useStockPack, setUseStockPack] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [autoPurchase, setAutoPurchase] = useState(false);

  // Deployability check state
  const [deployabilityLoading, setDeployabilityLoading] = useState(false);
  const [deployabilityResult, setDeployabilityResult] = useState(null);

  // Parse URL parameters for creation/update mode
  const [creationId, setCreationId] = useState(null);
  const creationIdRef = useRef(null);
  const [action, setAction] = useState("create");

  // Keep ref in sync with state so callbacks always use the latest creationId
  useEffect(() => {
    creationIdRef.current = creationId;
  }, [creationId]);

  // Create a ref to track if we've already initialized in creation mode
  const creationModeInitialized = useRef(false);

  // Ref to prevent double POST for Agent Deployment step
  const deploymentInProgressRef = useRef(false);

  // Ref to track if user manually selected a pack (to prevent auto-select override)
  const userSelectedPackRef = useRef(false);

  // Function to fetch wizard status from backend - wrapped in useCallback to avoid recreating on every render
  const fetchWizardStatus = useCallback(async (specificCreationId = null) => {
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      if (!accessToken) {
        console.error("No access token available");
        return;
      }
      
      // Add more detailed debugging
      console.log("fetchWizardStatus called with specificCreationId:", specificCreationId);
      console.log("Current creationId state:", creationId);
      
      // If we're in update mode with a specific creation ID, we can add it as a query parameter
      const url = specificCreationId 
        ? `/api/wizard/status/?creation_id=${specificCreationId}` 
        : `/api/wizard/status/`;
      
      console.log(`Fetching wizard status from: ${url}`);
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      
      console.log("Wizard status:", response.data);
      
      // If wizard has been started, update state with its status
      if (response.data.status !== "not_started") {
        // Set creation ID for future API calls (if not already set from URL)
        if (!specificCreationId) {
          setCreationId(response.data.creation_id);
          creationIdRef.current = response.data.creation_id;
        }
        
        // Update step statuses
        const newStepStatus = { ...stepStatus };
        Object.keys(response.data.steps).forEach(stepName => {
          newStepStatus[stepName] = response.data.steps[stepName].status;
        });
        setStepStatus(newStepStatus);
        stepStatusRef.current = newStepStatus;
        
        // Set data from completed steps
        const stepsData = response.data.steps;
        
        // Set Adexgénie data if completed
        if (stepsData["Adexgénie"] && stepsData["Adexgénie"].status === "completed") {
          const myGenieData = stepsData["Adexgénie"].data;
          console.log("Adexgénie step data received:", myGenieData);
          
          if (myGenieData.agentType) {
            console.log("Setting agent type from loaded data:", myGenieData.agentType);
            setAgentType(myGenieData.agentType);
            
            // Also set the service type and fetch service agent types
            if (myGenieData.agentType.id) {
              console.log("Setting service type from loaded agent type:", myGenieData.agentType);
              setServiceType({
                id: myGenieData.agentType.id,
                value_identifier: myGenieData.agentType.value_identifier || myGenieData.agentType.id
              });
              
              // Fetch service agent types for the loaded business type
              console.log("Fetching service agent types for loaded business type ID:", myGenieData.agentType.id);
              fetchServiceAgentTypes(myGenieData.agentType.id);
            }
          }
          if (myGenieData.agentName) {
            console.log("Setting agent name from loaded data:", myGenieData.agentName);
            console.log("Agent name details:", JSON.stringify(myGenieData.agentName, null, 2)); // Afficher la structure complète
            console.log("Type of agentName:", typeof myGenieData.agentName);
            
            // Assurez-vous que le nom de l'agent est correctement défini
            if (typeof myGenieData.agentName === 'string') {
              setAgentName(myGenieData.agentName);
              console.log("Agent name set as string:", myGenieData.agentName);
            } else if (myGenieData.agentName && myGenieData.agentName.name) {
              // Si c'est un objet avec une propriété 'name'
              setAgentName(myGenieData.agentName.name);
              console.log("Agent name set from object.name:", myGenieData.agentName.name);
            } else if (myGenieData.agentName && typeof myGenieData.agentName === 'object') {
              // Si c'est un objet, essayez de trouver une propriété qui pourrait contenir le nom
              const possibleNameProps = ['name', 'value', 'title', 'label'];
              for (const prop of possibleNameProps) {
                if (myGenieData.agentName[prop]) {
                  setAgentName(myGenieData.agentName[prop]);
                  console.log(`Agent name set from object.${prop}:`, myGenieData.agentName[prop]);
                  break;
                }
              }
            }
          } else {
            console.log("No agent name found in loaded data");
            // Si aucun nom n'est trouvé, on peut essayer de récupérer le nom directement depuis l'objet myGenieData
            if (myGenieData.name) {
              setAgentName(myGenieData.name);
              console.log("Agent name set from myGenieData.name:", myGenieData.name);
            }
          }
          if (myGenieData.language) {
            console.log("Setting language from loaded data:", myGenieData.language);
            setLanguage(myGenieData.language);
          }
          if (myGenieData.aiAgentType) setAiAgentType(myGenieData.aiAgentType);
          if (myGenieData.platformMode) setPlatformMode(myGenieData.platformMode);
          if (myGenieData.billingCycle) setBillingCycle(myGenieData.billingCycle);
          if (myGenieData.currency) setCurrency(myGenieData.currency);
          if (myGenieData.temperature !== undefined) setTemperature(myGenieData.temperature);
          if (myGenieData.maxTokens !== undefined) setMaxTokens(myGenieData.maxTokens);
          if (myGenieData.brandInfo) {
            const bi = myGenieData.brandInfo;
            if (bi.brand_name !== undefined) setBrandName(bi.brand_name || '');
            if (bi.brand_description !== undefined) setBrandDescription(bi.brand_description || '');
            if (bi.brand_website !== undefined) setBrandWebsite(bi.brand_website || '');
            if (bi.business_hours !== undefined) setBusinessHours(bi.business_hours || '');
            if (bi.brand_contact_email !== undefined) setBrandContactEmail(bi.brand_contact_email || '');
            if (bi.brand_contact_phone !== undefined) setBrandContactPhone(bi.brand_contact_phone || '');
          }
        }
        
        // Set Phone Number data if completed
        if (stepsData["Phone Number"] && stepsData["Phone Number"].status === "completed") {
          const phoneData = stepsData["Phone Number"].data;
          if (phoneData.phoneNumber) {
            setTwilioProvisionedNumber(phoneData.phoneNumber);
          }
        }
        
        // Set Services data if completed
        if (stepsData["Services"] && stepsData["Services"].status === "completed") {
          const servicesData = stepsData["Services"].data;
          console.log("[DEBUG] Services data loaded:", servicesData);
          
          if (servicesData.serviceAgentType) {
            console.log("[DEBUG] Service agent type from loaded data:", servicesData.serviceAgentType);
            
            // Store the service agent type data for later matching
            // This will be used when serviceAgentTypes are loaded
            window.servicesDataFromBackend = servicesData;
            
            // Assurez-vous que serviceAgentType est un objet complet avec toutes les propriétés nécessaires
            // Si c'est juste un ID ou un identifiant, nous devrons le transformer en objet complet
            if (typeof servicesData.serviceAgentType === 'string' || 
                (typeof servicesData.serviceAgentType === 'object' && !servicesData.serviceAgentType.id)) {
              console.log("[DEBUG] Service agent type is a string or incomplete object, will match later when types are loaded");
              
              // If we already have service agent types loaded, try to match now
              if (serviceAgentTypes.length > 0) {
                const serviceTypeId = typeof servicesData.serviceAgentType === 'string' 
                  ? servicesData.serviceAgentType 
                  : servicesData.serviceAgentType.value_identifier;
                
                console.log("[DEBUG] Looking for service type with ID:", serviceTypeId);
                
                const matchedType = serviceAgentTypes.find(type => 
                  type.id === serviceTypeId || 
                  type.value_identifier === serviceTypeId
                );
                
                if (matchedType) {
                  console.log("[DEBUG] Found matching service agent type:", matchedType);
                  setServiceAgentType(matchedType);
                } else {
                  console.log("[DEBUG] No matching service agent type found in:", serviceAgentTypes);
                }
              }
            } else {
              // Si c'est déjà un objet complet, utilisez-le directement
              console.log("[DEBUG] Service agent type is a complete object, using directly:", servicesData.serviceAgentType);
              setServiceAgentType({
                ...servicesData.serviceAgentType,
                capabilities: parseCapabilities(servicesData.serviceAgentType.capabilities)
              });
            }
          }
          
          if (servicesData.services) {
            setServices(servicesData.services);
          }
        }
        
        // Set Files data if completed
        if (stepsData["Files"] && stepsData["Files"].status === "completed") {
          const filesData = stepsData["Files"].data;
          if (filesData.files) {
            setFileStatuses(filesData.files.reduce((acc, file) => {
              acc[file.name] = { status: "success", id: file.id };
              return acc;
            }, {}));
          }
        }
        
        // Set Agent Deployment data if completed (or in_progress in update mode)
        // Use local variables to track resolved values and avoid state closure issues
        // Priority: response root (from linked_agent DB) > step data (may be stale)
        let resolvedAgentId = response.data.agent_id || null;
        let resolvedPackId = response.data.company_pack_id || null;
        if (resolvedAgentId) setCurrentAgentId(resolvedAgentId);
        if (resolvedPackId) setCurrentPackId(resolvedPackId);
        const deployStep = stepsData["Agent Deployment"];
        if (deployStep && (deployStep.status === "completed" || (action === 'update' && deployStep.status === "in_progress"))) {
          const deploymentData = deployStep.data || {};
          if (deploymentData.adexcloud_agent_id || deploymentData.assistant_id) {
            setAgentExternalId(deploymentData.adexcloud_agent_id || deploymentData.assistant_id);
          }
          // Fallback: if response root didn't have agent_id, use step data
          if (!resolvedAgentId && deploymentData.agent_id) {
            resolvedAgentId = deploymentData.agent_id;
            setCurrentAgentId(deploymentData.agent_id);
          }
          // Fallback: if response root didn't have company_pack_id, use step data
          if (!resolvedPackId && deploymentData.company_pack_id) {
            resolvedPackId = deploymentData.company_pack_id;
            setCurrentPackId(deploymentData.company_pack_id);
          } else if (!resolvedPackId && deploymentData.agent_id) {
            // Last resort fallback: fetch agents list to get current pack
            try {
              const agentsResp = await axios.get('/api/agents/list/', {
                headers: { Authorization: `Bearer ${accessToken}` },
              });
              const agents = Array.isArray(agentsResp.data) ? agentsResp.data : (agentsResp.data?.agents || agentsResp.data?.data || []);
              const currentAgent = agents.find(a => a.id === deploymentData.agent_id || a.agent_id === deploymentData.agent_id);
              if (currentAgent?.company_pack_id) {
                resolvedPackId = currentAgent.company_pack_id;
                setCurrentPackId(currentAgent.company_pack_id);
              }
            } catch (agentErr) {
              console.error('Failed to load agent pack info:', agentErr);
            }
          }
          // Load existing KB/WF assignments for this agent in update mode
          if (deploymentData.agent_id) {
            try {
              const agentKbsResp = await axios.get('/api/knowledge-bases/', {
                headers: { Authorization: `Bearer ${accessToken}` },
              });
              const allKbs = agentKbsResp.data?.data || [];
              const agentKbs = allKbs.filter(kb => kb.assistant_id === deploymentData.agent_id);
              if (agentKbs.length > 0) {
                setWizardKbs(agentKbs);
                if (agentKbs.length > 0 && !wizardKb) {
                  setWizardKb(agentKbs[0]);
                }
              }
            } catch (kbErr) {
              console.error('Failed to load existing agent KBs:', kbErr);
            }
            try {
              const agentWfsResp = await axios.get('/api/workflows/', {
                headers: { Authorization: `Bearer ${accessToken}` },
              });
              const allWfs = agentWfsResp.data?.data || [];
              const agentWfs = allWfs.filter(wf => wf.assigned_agent_id === deploymentData.agent_id);
              if (agentWfs.length > 0) {
                setWizardWorkflows(agentWfs);
              }
            } catch (wfErr) {
              console.error('Failed to load existing agent workflows:', wfErr);
            }
          }
        }
        
        // Navigate to the next incomplete step if any
        if (response.data.next_step) {
          const nextStepIndex = steps.findIndex(step => step.title === response.data.next_step);
          if (nextStepIndex !== -1) {
            setActiveStepIndex(nextStepIndex);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching wizard status:", error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, stepStatus, creationId, serviceAgentTypes]);

  // Parse URL parameters on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const creationIdParam = queryParams.get('creationId');
    const actionParam = queryParams.get('action');
    
    if (creationIdParam && actionParam === 'update') {
      console.log(`Wizard in update mode with creation ID: ${creationIdParam}`);
      setCreationId(creationIdParam);
      creationIdRef.current = creationIdParam;
      setAction('update');
      
      // In update mode, we'll fetch the wizard status using the creation ID from URL
      fetchWizardStatus(creationIdParam);
    } else {
      // Only initialize creation mode once
      if (!creationModeInitialized.current) {
        console.log('Wizard in creation mode - starting fresh');
        setAction('create');
        
        // In creation mode, we always start fresh - no need to check for in-progress wizards
        // Reset all form states to ensure a clean start
        setAgentType(null);
        setAgentName('');
        setBrandName('');
        setBrandDescription('');
        setBrandWebsite('');
        setBusinessHours('');
        setBrandContactEmail('');
        setBrandContactPhone('');
        setTwilioProvisionedNumber('');
        setServiceAgentType(null);
        setServices([]);
        setFileStatuses({});
        setAgentExternalId('');
        setWidgetCode(null);
        
        // Reset step statuses
        setStepStatus({
          "Pack": "not_started",
          "Adexgénie": "not_started",
          "Services": "not_started",
          "Phone Number": "not_started",
          "Files": "not_started",
          "Deployability Check": "not_started"
        });
        
        // Set active step to the first step
        setActiveStepIndex(0);
        
        // Mark as initialized
        creationModeInitialized.current = true;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // We want this to run only once on mount, so we use an empty dependency array and disable the lint warning

  // State for tracking if business types are being loaded
  const [isLoadingBusinessTypes, setIsLoadingBusinessTypes] = useState(false);
  const [businessTypesLoaded, setBusinessTypesLoaded] = useState(false);

  const [businessTypes, setBusinessTypes] = useState([]); // State to hold business types

  // Fetch business types from backend
  useEffect(() => {
    // Skip if already loaded or currently loading
    if (businessTypesLoaded || isLoadingBusinessTypes) {
      return;
    }

    const fetchBusinessTypes = async () => {
      try {
        setIsLoadingBusinessTypes(true);
        // Use the auth context to get the token instead of directly accessing localStorage
        const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
        
        if (!accessToken) {
          console.error("No access token available");
          // If using the auth context, you might want to trigger a login redirect here
          setIsLoadingBusinessTypes(false);
          return;
        }
        
        console.log("Using token for API call");
        
        // Use the axios instance with interceptors that's set up in the useEffect below
        // This will automatically handle token refresh if needed
        let response;
        try {
          response = await axios.get(`/api/get-business-types/`, {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          });
        } catch (error) {
          if (error.response && error.response.status === 404) {
            // If 404, try without trailing slash
            response = await axios.get(`/api/get-business-types`, {
              headers: {
                Authorization: `Bearer ${accessToken}`
              }
            });
          } else if (error.response && error.response.status === 401 && refreshToken) {
            // If 401 and we have a refresh function, try to refresh the token and retry
            try {
              const newToken = await refreshToken();
              if (newToken) {
                // Retry with the new token
                response = await axios.get(`/api/get-business-types/`, {
                  headers: {
                    Authorization: `Bearer ${newToken}`
                  }
                });
              } else {
                throw new Error("Failed to refresh token");
              }
            } catch (refreshError) {
              console.error("Token refresh failed:", refreshError);
              throw error; // Rethrow the original error
            }
          } else {
            throw error;
          }
        }
        
        // Map local illustration images by business type
        const imageMap = {
          receptionist: '/agent_receptionnist.png',
          support: '/agent_support.png',
          sales: '/agent_commerciale.png',
        };

        // Transform backend data to match Cards component expectations
        const transformedTypes = response.data.map(type => ({
          id: type.id, // Use the actual UUID as the primary ID
          name: type.name,
          description: type.description,
          icon: type.icon,
          image_url: imageMap[type.value_identifier] || type.image_url || '',
          capabilities: parseCapabilities(type.capabilities), // Normalize capabilities from string or array
          value_identifier: type.value_identifier // Keep original identifier if needed elsewhere
        }));
        setBusinessTypes(transformedTypes);
        setBusinessTypesLoaded(true);
        console.log("Loaded business types:", transformedTypes);
      } catch (error) {
        console.error("Error fetching business types:", error);
        // Show a more user-friendly error message
        setBusinessTypes([{
          id: "error",
          name: "Error Loading Business Types",
          description: "There was a problem loading business types. Please refresh the page or check your connection.",
          icon: "error",
          capabilities: []
        }]);
      } finally {
        setIsLoadingBusinessTypes(false);
      }
    };

    fetchBusinessTypes();
  }, [user, refreshToken, businessTypesLoaded, isLoadingBusinessTypes]); // Add tracking states to dependencies

  // Fetch available packs
  useEffect(() => {
    const fetchPacks = async () => {
      try {
        setLoadingPacks(true);
        const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
        if (!accessToken) {
          setLoadingPacks(false);
          return;
        }
        const response = await axios.get('/api/company-packs/', {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (response.data?.status === 'success' && Array.isArray(response.data.data)) {
          setAvailablePacks(response.data.data);
          // In update mode, prefer the agent's current pack; otherwise pick first available
          const defaultPack = action === 'update' && currentPackId
            ? response.data.data.find(p => p.id === currentPackId)
            : response.data.data.find(p => p.quotas?.agents?.remaining > 0) || response.data.data[0];
          if (defaultPack && !selectedPack) {
            setSelectedPack(defaultPack);
          }
        }
      } catch (error) {
        console.error('Error fetching packs:', error);
      } finally {
        setLoadingPacks(false);
      }
    };

    if (user) {
      fetchPacks();
    }
  }, [user, setAvailablePacks, setLoadingPacks, setSelectedPack, currentPackId, action]);

  // In update mode, auto-select the agent's current pack once both packs and currentPackId are loaded
  // Skip if user has manually selected a pack
  useEffect(() => {
    if (action === 'update' && currentPackId && availablePacks.length > 0 && !userSelectedPackRef.current) {
      const currentPack = availablePacks.find(p => p.id === currentPackId);
      if (currentPack && selectedPack?.id !== currentPackId) {
        setSelectedPack(currentPack);
      }
    }
  }, [action, currentPackId, availablePacks, selectedPack]);

  // Fetch existing knowledge bases
  const fetchExistingKbs = useCallback(async () => {
    try {
      setLoadingExistingKbs(true);
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      if (!accessToken) {
        setLoadingExistingKbs(false);
        return;
      }
      const resp = await axios.get('/api/knowledge-bases/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const raw = resp.data?.data || resp.data || [];
      const syncedKbs = (Array.isArray(raw) ? raw : []).filter(
        (kb) => kb.adexcloud_kb_id && kb.sync_status !== 'failed'
      );
      setExistingKbs(syncedKbs.map(kb => ({ label: kb.name, value: kb.id, data: kb })));
    } catch (err) {
      console.error('Error fetching existing KBs:', err);
    } finally {
      setLoadingExistingKbs(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchExistingKbs();
    }
  }, [user, fetchExistingKbs]);

  // Fetch existing workflows
  const fetchExistingWorkflows = useCallback(async () => {
    try {
      setLoadingExistingWorkflows(true);
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      if (!accessToken) {
        setLoadingExistingWorkflows(false);
        return;
      }
      const resp = await axios.get('/api/workflows/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const raw = resp.data?.data || resp.data || [];
      const syncedWfs = (Array.isArray(raw) ? raw : []).filter(
        (wf) => wf.adexcloud_workflow_id && wf.sync_status !== 'failed'
      );
      setExistingWorkflows(syncedWfs.map(wf => ({ label: wf.name, value: wf.id, data: wf })));
    } catch (err) {
      console.error('Error fetching existing workflows:', err);
    } finally {
      setLoadingExistingWorkflows(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchExistingWorkflows();
    }
  }, [user, fetchExistingWorkflows]);

  // Real deployability check against the selected pack quota
  const checkDeployability = useCallback(async () => {
    if (!selectedPack) return;
    setDeployabilityLoading(true);
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      let quotaUrl = `/api/billing/quota/?company_pack_id=${selectedPack.id}`;
      if (action === 'update' && currentAgentId) {
        quotaUrl += `&agent_id=${currentAgentId}`;
      }
      const response = await axios.get(quotaUrl, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      const quotaData = response.data?.data;
      setDeployabilityResult({
        deployable: !!quotaData?.can_create,
        message: quotaData?.message || (quotaData?.can_create ? 'All quotas are sufficient.' : 'Pack quota exceeded.'),
        details: quotaData?.details || null
      });
    } catch (error) {
      console.error('Deployability check error:', error);
      setDeployabilityResult({
        deployable: false,
        message: 'Unable to verify quota. Please try again.'
      });
    } finally {
      setDeployabilityLoading(false);
    }
  }, [selectedPack, user, action, currentAgentId]);

  // LiveKit-ready call functions (replace with SDK integration when available)
  const startAgentCall = async () => {
    if (agentExternalId) {
      try {
        setIsCallLoading(true);
        const tokenRes = await axios.post(
          "/api/agent/test-call/",
          { agent_id: agentExternalId },
          { withCredentials: true }
        );
        const { token, livekit_url, room } = tokenRes.data;
        setLivekitToken(token);
        setCallId(room);
        setCallStatus("active");
        setIsCallLoading(false);
        console.log("LiveKit token received:", token, "url:", livekit_url, "room:", room);
      } catch (error) {
        console.error("Failed to start call:", error);
        setCallStatus("error");
        setIsCallLoading(false);
        notifyError("Unable to start call: " + (error.message || "Unknown error"));
      }
    } else {
      console.error("Cannot start call: no agent external ID");
    }
  };

  const stopAgentCall = () => {
    try {
      if (websocket && typeof websocket.close === 'function') websocket.close();
      setAvatarReady(false);
      if (talkingHead && typeof talkingHead.stop === 'function') talkingHead.stop();
      setCallStatus("idle");
      setVolumeLevel(0);
      setLivekitToken(null);
      console.log("Call stopped successfully");
    } catch (error) {
      console.error("Error stopping call:", error);
      setCallStatus("idle");
      setVolumeLevel(0);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      notifySuccess('Copied to clipboard');
    } catch (err) {
      console.error('Copy failed:', err);
      notifyError('Unable to copy');
    }
  };

  useEffect(() => {
    console.log("Updated fileidx:", fileidx); // Runs AFTER state update
  }, [fileidx]);

  const handleCreateWorkflowInWizard = async () => {
    if (!wfForm.name.trim()) {
      notifyError('Workflow name is required');
      return;
    }
    setResourceSubmitLoading(true);
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      const isMcp = wfForm.workflow_type === 'mcp';
      const configJson = {
        mode: 'DEDICATED',
        billing_cycle: 'year',
        status: 'pending',
        workflow_type: isMcp ? 'mcp' : 'webhook',
      };
      if (isMcp) {
        configJson.mcp_config = {};
        if (wfForm.url.trim()) configJson.mcp_config.mcp_server_url = wfForm.url.trim();
        if (wfForm.api_key.trim()) configJson.mcp_config.mcp_api_key = wfForm.api_key.trim();
      } else {
        if (wfForm.url.trim()) configJson.webhook_url = wfForm.url.trim();
        if (wfForm.api_key.trim()) configJson.api_key = wfForm.api_key.trim();
      }
      if (wfForm.action.trim()) configJson.action = wfForm.action.trim();
      const inputs = {};
      wfForm.params.forEach((p) => {
        if (p.key.trim()) inputs[p.key.trim()] = p.defaultValue;
      });
      if (Object.keys(inputs).length > 0) configJson.inputs = inputs;

      const payload = {
        name: wfForm.name.trim(),
        description: wfForm.description.trim(),
        service_type: isMcp ? 'mcp_tool' : 'workflow',
        mcp_enabled: isMcp,
        platform_mode: 'dedicated',
        billing_cycle: 'yearly',
        status: 'pending',
        config_json: configJson,
      };
      // In the wizard, the agent is not yet created: reserve the workflow on the selected pack instance
      if (selectedPack?.id) {
        payload.company_pack_id = selectedPack.id;
      }
      const resp = await axios.post('/api/workflows/create/', payload, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      let wfResp = resp.data;
      if (wfResp?.status === 'confirmation_required') {
        payload.confirm_resource_activation = true;
        const confirmResp = await axios.post('/api/workflows/create/', payload, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        wfResp = confirmResp.data;
      }
      if (wfResp?.status === 'sync_failed') {
        notifyError(wfResp.message || 'Workflow created locally but failed to sync to adexcloud. Retry sync from the workflow list.');
        setWfModalVisible(false);
        return;
      }
      const newWf = wfResp?.data;
      if (newWf) {
        setWizardWorkflows((prev) => [...prev, newWf]);
        notifySuccess(`Workflow "${newWf.name}" created successfully`);
      }
      setWfModalVisible(false);
      setWfForm({
        name: '',
        description: '',
        workflow_type: 'webhook',
        url: '',
        api_key: '',
        action: '',
        params: [],
      });
    } catch (err) {
      notifyError('Create workflow failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setResourceSubmitLoading(false);
    }
  };

  const handleCreateKbInWizard = async () => {
    if (!kbForm.name.trim()) {
      notifyError('Knowledge base name is required');
      return;
    }
    setResourceSubmitLoading(true);
    try {
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      const payload = {
        name: kbForm.name.trim(),
        description: kbForm.description.trim(),
        platform_mode: 'dedicated',
        billing_cycle: 'yearly',
        config_json: {
          chunking: {
            chunk_size: 1000,
            chunk_overlap: 200
          }
        },
      };
      // In the wizard, the agent is not yet created: reserve the KB on the selected pack instance
      if (selectedPack?.id) {
        payload.company_pack_id = selectedPack.id;
      }
      const resp = await axios.post('/api/knowledge-bases/create/', payload, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (resp.data?.status === 'sync_failed') {
        notifyError(resp.data.message || 'KB created locally but failed to sync to adexcloud. Retry sync from the KB list.');
        setKbModalVisible(false);
        return;
      }
      if (resp.data?.status === 'confirmation_required') {
        // Pack needs activation — resend with confirmation flag
        payload.confirm_resource_activation = true;
        const confirmResp = await axios.post('/api/knowledge-bases/create/', payload, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (confirmResp.data?.status === 'sync_failed') {
          notifyError(confirmResp.data.message || 'KB created locally but failed to sync to adexcloud.');
          setKbModalVisible(false);
          return;
        }
        const confirmedKb = confirmResp.data?.data;
        if (confirmedKb && confirmedKb.adexcloud_kb_id) {
          setWizardKb(confirmedKb);
          setWizardKbs((prev) => [...prev, confirmedKb]);
          notifySuccess(`Knowledge base "${confirmedKb.name}" created successfully`);
          fetchExistingKbs();
        } else if (confirmedKb && !confirmedKb.adexcloud_kb_id) {
          notifyError('KB created locally but not synced to adexcloud. Please retry sync from the KB list.');
          fetchExistingKbs();
        }
        setKbModalVisible(false);
        setKbForm({ name: '', description: '' });
        return;
      }
      const newKb = resp.data?.data;
      if (newKb && newKb.adexcloud_kb_id) {
        setWizardKb(newKb);
        setWizardKbs((prev) => [...prev, newKb]);
        notifySuccess(`Knowledge base "${newKb.name}" created successfully`);
        fetchExistingKbs();
      } else if (newKb && !newKb.adexcloud_kb_id) {
        notifyError('KB created locally but not synced to adexcloud. Please retry sync from the KB list before uploading files.');
        fetchExistingKbs();
      }
      setKbModalVisible(false);
      setKbForm({ name: '', description: '' });
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.status === 'sync_failed') {
        notifyError(errData.message || 'KB created locally but failed to sync to adexcloud. Retry sync from the KB list.');
        fetchExistingKbs();
      } else {
        notifyError('Create KB failed: ' + (errData?.message || err.message));
      }
      setKbModalVisible(false);
    } finally {
      setResourceSubmitLoading(false);
    }
  };

  const handleUploadFiles = async () => {
    setIsLoading(true);
    setError(null);
    console.log("Starting file upload process...");

    try {
      if (fileskn.length === 0) {
        console.log("No files selected, aborting upload");
        setIsLoading(false);
        return [];
      }

      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      if (!accessToken) {
        setError('Authentication error. Please log in again.');
        setIsLoading(false);
        return [];
      }

      // KB must be selected/created and synced to adexcloud before uploading files
      const kbId = wizardKb?.id || wizardKb?.adexcloud_kb_id;
      if (!kbId) {
        setError('Please select or create a knowledge base before uploading files.');
        setIsLoading(false);
        return [];
      }
      if (!wizardKb?.adexcloud_kb_id) {
        setError('This knowledge base is not synced to adexcloud. Please retry sync from the KB list before uploading files.');
        setIsLoading(false);
        return [];
      }

      // 2. Upload each file to the KB via /api/knowledge-bases/{kbId}/upload/
      const uploadFileIds = [];
      for (const file of fileskn) {
        try {
          console.log(`Uploading file: ${file.name}`);
          const formData = new FormData();
          formData.append('file', file);

          const uploadResp = await axios.post(
            `/api/knowledge-bases/${kbId}/upload/`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'multipart/form-data',
              },
            }
          );

          const serverFileId = uploadResp.data?.data?.file_id;
          if (serverFileId) {
            uploadFileIds.push(serverFileId);
            setFileStatuses((prev) => ({
              ...prev,
              [file.name]: { status: 'pending', id: serverFileId },
            }));
          } else {
            setFileStatuses((prev) => ({
              ...prev,
              [file.name]: { status: 'error', error: 'No file_id returned' },
            }));
          }
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          setFileStatuses((prev) => ({
            ...prev,
            [file.name]: { status: 'error', error: error.response?.data?.message || error.message },
          }));
        }
      }

      if (uploadFileIds.length === 0) {
        throw new Error('No files were successfully uploaded');
      }

      // 3. Poll for file processing status
      console.log("Polling for file processing status...");
      const pollStatus = () =>
        new Promise((resolve) => {
          let elapsed = 0;
          let consecutiveErrors = 0;
          // adexcloud file ingestion can take a long time; poll for 30 minutes
          const maxWait = 1800000;
          const interval = setInterval(async () => {
            elapsed += 3000;
            try {
              const results = await Promise.all(
                uploadFileIds.map((fid) =>
                  axios.get(`/api/knowledge-bases/${kbId}/files/${fid}/status/`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                  })
                )
              );
              consecutiveErrors = 0;

              // Update per-file statuses with latest state (by name or by id fallback)
              results.forEach((r) => {
                const d = r.data?.data;
                const s = d?.status;
                const n = d?.name;
                const fid = d?.file_id || d?.id;
                if (n) {
                  setFileStatuses((prev) => ({
                    ...prev,
                    [n]: {
                      status: s === 'failed' ? 'error' : s || 'in-progress',
                      id: fid,
                      error: d?.error || d?.adexcloud_status?.processing?.error || null,
                      processing: d?.processing || d?.adexcloud_status?.processing || null,
                      cost_analysis: d?.cost_analysis || null,
                    },
                  }));
                }
              });

              const anyFailed = results.some((r) => {
                const s = r.data?.data?.status;
                return s === 'failed';
              });
              const allDone = results.every((r) => {
                const s = r.data?.data?.status;
                return s === 'success' || s === 'completed' || s === 'failed';
              });

              if (anyFailed || allDone || elapsed >= maxWait) {
                clearInterval(interval);
                resolve({ anyFailed, allDone, results, timedOut: elapsed >= maxWait && !anyFailed && !allDone });
              }
            } catch (err) {
              console.error('Status poll error:', err);
              consecutiveErrors += 1;
              // Keep polling unless we get many consecutive errors or time out
              if (consecutiveErrors >= 5 || elapsed >= maxWait) {
                clearInterval(interval);
                resolve({ anyFailed: consecutiveErrors >= 5, allDone: false, error: err, timedOut: elapsed >= maxWait });
              }
            }
          }, 3000);
        });
      const pollResult = await pollStatus();

      setFileidx(uploadFileIds);

      // Mark non-terminal files as still processing after maxWait
      if (pollResult.timedOut) {
        setFileStatuses((prev) => {
          const next = { ...prev };
          uploadFileIds.forEach((fid) => {
            const name = fileskn.find((f) => f.name && prev[f.name]?.id === fid)?.name;
            if (name && (next[name].status === 'in-progress' || next[name].status === 'processing' || next[name].status === 'uploaded' || next[name].status === 'pending')) {
              next[name] = { ...next[name], status: 'processing', info: 'Still processing on adexcloud. Check back later.' };
            }
          });
          return next;
        });
        return uploadFileIds;
      }

      if (pollResult.anyFailed) {
        // Show error summary
        const failedFiles = pollResult.results
          ?.filter((r) => r.data?.data?.status === 'failed')
          .map((r) => r.data?.data?.name || 'Unknown file');
        const msg = failedFiles?.length
          ? `Processing failed for: ${failedFiles.join(', ')}. Please retry sync or contact support if adexcloud services are down.`
          : 'File processing failed on adexcloud. Please check your knowledge base status.';
        setError(msg);
        console.error(msg, pollResult.results);
        return uploadFileIds;
      }

      console.log("Upload complete. KB:", kbId, "Files:", uploadFileIds);
      return uploadFileIds;
    } catch (error) {
      console.error("Error in handleUploadFiles:", error);
      setError(error.message);
      return [];
    } finally {
      console.log("Upload process complete, resetting loading state");
      setIsLoading(false);
    }
  };

  const getTokenStatus = (file) => {
    const status = fileStatuses[file.name];
    if (!status) return "pending";
    if (status.status === "success" || status.status === "completed") return "success";
    if (status.status === "error" || status.status === "failed") return "error";
    return "pending";
  };

  // Generate wave bars based on volume level
  const generateWaveBars = (volumeLevel) => {
    return (
      <div className="voice-circle-container">
        <div
          className="main-circle"
          style={{
            transform: `scale(${0.6 + volumeLevel * 0.6})`,
            background: `radial-gradient(circle at 65% 35%, 
rgb(8, 112, 76) 0%, 
rgb(13, 109, 90) ${40 + volumeLevel * 30}%, 
rgb(13, 80, 62) 120%)`,
          }}
        >
          <div className="inner-pulse" />
          <div className="sonar-wave" />
          <div className="particle-overlay" />
        </div>
      </div>
    );
  };

  const handleAgentTypeSelect = (selectedType) => {
    console.log("Selected business type:", selectedType);
    setAgentType(selectedType || null);
    
    // Set the service type value identifier for API calls
    if (selectedType) {
      // Store the complete business type object instead of just the value identifier
      setServiceType({
        id: selectedType.id,
        value_identifier: selectedType.value_identifier || selectedType.id
      });
      
      // Fetch service agent types for the selected business type
      console.log("Fetching service agent types with business type ID:", selectedType.id);
      fetchServiceAgentTypes(selectedType.id);
    } else {
      // Reset service agent types if no business type is selected
      setServiceAgentTypes([]);
      setServiceAgentType(null);
      setServiceType({});
    }
  };
  
  const fetchServiceAgentTypes = async (businessTypeId) => {
    if (!businessTypeId) {
      console.error("No business type ID provided");
      setServiceAgentTypes([]);
      setLoadingServiceAgentTypes(false);
      return;
    }
    
    setLoadingServiceAgentTypes(true);
    console.log("Fetching service agent types for business type ID:", businessTypeId);
    
    try {
      // Try to determine if we have an ID or a value_identifier
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(businessTypeId);
      const identifier = isUUID ? businessTypeId : (typeof businessTypeId === 'string' ? businessTypeId : businessTypeId.toString());
      
      console.log(`Using ${isUUID ? 'UUID' : 'value_identifier'}: ${identifier} to fetch service agent types`);
      
      const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
      const response = await axios.get(
        `/api/get-service-agent-types/${identifier}/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      console.log("Service agent types API response:", response);
      
      if (response.data && Array.isArray(response.data)) {
        console.log("Received service agent types:", response.data);
        
        // Store the current serviceAgentType before updating the list
        const currentServiceAgentType = serviceAgentType;
        
        // Normalize capabilities and update the service agent types list
        const normalizedServiceAgentTypes = response.data.map(type => ({
          ...type,
          capabilities: parseCapabilities(type.capabilities)
        }));
        setServiceAgentTypes(normalizedServiceAgentTypes);
        
        // If we're in update mode and have a previously selected service agent type
        if (action === "update" && currentServiceAgentType) {
          console.log("[DEBUG] In update mode with existing service agent type:", currentServiceAgentType);
          
          // Try to find a match in the new list
          const matchedType = normalizedServiceAgentTypes.find(type => 
            (currentServiceAgentType.id && type.id === currentServiceAgentType.id) || 
            (currentServiceAgentType.value_identifier && type.value_identifier === currentServiceAgentType.value_identifier)
          );
          
          if (matchedType) {
            console.log("[DEBUG] Found matching service agent type in new list:", matchedType);
            setServiceAgentType(matchedType);
          } else {
            console.log("[DEBUG] No matching service agent type found in new list");
            // Only reset if no match is found
            setServiceAgentType(null);
          }
        } else if (action === "update") {
          // If we're in update mode but don't have a service agent type yet,
          // check if we have one in the completed steps data
          const steps = JSON.parse(localStorage.getItem("wizardSteps") || "{}");
          const servicesData = steps["Services"] && steps["Services"].data;
          
          if (servicesData && servicesData.serviceAgentType) {
            console.log("[DEBUG] Found service agent type in completed steps:", servicesData.serviceAgentType);
            
            const serviceTypeId = typeof servicesData.serviceAgentType === 'string' 
              ? servicesData.serviceAgentType 
              : servicesData.serviceAgentType.id || servicesData.serviceAgentType.value_identifier;
            
            console.log("[DEBUG] Looking for service type with ID:", serviceTypeId);
            
            const matchedType = response.data.find(type => 
              type.id === serviceTypeId || 
              type.value_identifier === serviceTypeId
            );
            
            if (matchedType) {
              console.log("[DEBUG] Found matching service agent type for completed step:", matchedType);
              setServiceAgentType(matchedType);
            } else {
              console.log("[DEBUG] No matching service agent type found for completed step");
              setServiceAgentType(null);
            }
          } else {
            // No service agent type in completed steps
            setServiceAgentType(null);
          }
        } else {
          // In create mode, reset the selection
          setServiceAgentType(null);
        }
      } else {
        console.error("Unexpected response format:", response.data);
        setServiceAgentTypes([]);
        setServiceAgentType(null);
      }
    } catch (error) {
      console.error("Error fetching service agent types:", error);
      console.error("Error details:", error.response ? error.response.data : "No response data");
      // Fallback to empty array if API fails
      setServiceAgentTypes([]);
    } finally {
      setLoadingServiceAgentTypes(false);
    }
  };
  
  const handleServiceAgentTypeSelect = (selectedType) => {
    console.log("Selected service agent type:", selectedType);
    setServiceAgentType(selectedType || null);

    // Log the selection for debugging
    if (selectedType) {
      console.log("Service agent type selected:", {
        id: selectedType.id,
        name: selectedType.name,
        value_identifier: selectedType.value_identifier
      });
    } else {
      console.log("No service agent type selected");
    }
  };

  const findSelectedServiceAgentTypeIndex = useCallback(() => {
    if (!serviceAgentType || serviceAgentTypes.length === 0) return -1;
    
    // Essayer de trouver par ID d'abord
    if (serviceAgentType.id) {
      const index = serviceAgentTypes.findIndex(type => type.id === serviceAgentType.id);
      if (index !== -1) return index;
    }
    
    // Essayer de trouver par value_identifier ensuite
    if (serviceAgentType.value_identifier) {
      const index = serviceAgentTypes.findIndex(type => 
        type.value_identifier === serviceAgentType.value_identifier);
      if (index !== -1) return index;
    }
    
    // Essayer de trouver par nom si tout le reste échoue
    if (serviceAgentType.name) {
      const index = serviceAgentTypes.findIndex(type => type.name === serviceAgentType.name);
      if (index !== -1) return index;
    }
    
    return -1;
  }, [serviceAgentType, serviceAgentTypes]);

  const steps = useMemo(() => [
    {
      title: "Pack",
      info: <Icon name="shopping-cart" variant="success" />,
      description: "Select your commercial pack",
      content: (
        <Container header={<Header variant="h3"><Icon name="shopping-cart" size="medium" /> Select Your Pack</Header>}>
          <SpaceBetween size="l">
            <Alert type="info">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Icon name="info" size="big" variant="subtle" />
                <span style={{ marginLeft: '10px' }}>
                  Start by selecting a commercial pack to define this agent's quotas and resource limits.
                </span>
              </div>
            </Alert>

            {loadingPacks ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <Spinner />
              </div>
            ) : availablePacks.length === 0 ? (
              <Alert type="warning" header="No packs available">
                Please contact an administrator to configure commercial packs.
              </Alert>
            ) : action === "update" ? (
              <SpaceBetween size="l">
                <FormField
                  label="Commercial Pack"
                  description="Select the active pack for this agent."
                >
                  <Cards
                    selectionType="single"
                    trackBy="id"
                    entireCardClickable
                    cardsPerRow={[{ cards: 1 }, { minWidth: 640, cards: 2 }, { minWidth: 1100, cards: 3 }]}
                    onSelectionChange={({ detail }) => {
                      const pack = detail.selectedItems[0];
                      setSelectedPack(pack);
                      userSelectedPackRef.current = true;
                      setUseStockPack(true);
                      setAutoPurchase(false);
                      console.log('Selected pack:', pack);
                    }}
                    selectedItems={selectedPack ? [selectedPack] : []}
                    items={availablePacks.filter(p => p.quotas?.agents?.remaining > 0 || p.id === currentPackId).map(pack => {
                      const isVoice = aiAgentType === 'voice';
                      const pd = pack.pack_details || {};
                      const usageLabel = isVoice
                        ? `${pd.cycle_minutes || 0} voice minutes/${pd.cycle_label || 'month'}`
                        : `${pd.cycle_conversations || 0} text conversations/${pd.cycle_label || 'month'}`;
                      const kbTotal = pack.quotas?.knowledge_bases?.total || 0;
                      const kbUsed = pack.quotas?.knowledge_bases?.used || 0;
                      const wfTotal = pack.quotas?.workflows?.total || 0;
                      const wfUsed = pack.quotas?.workflows?.used || 0;
                      const resourceLabel = `KB: ${kbUsed}/${kbTotal} | WF: ${wfUsed}/${wfTotal}`;
                      const statusLabel = pack.status === 'inventory' ? 'In stock' : (pack.has_agent ? 'Assigned' : 'Active');
                      return {
                        ...pack,
                        name: pack.name,
                        description: pack.pack_name,
                        usageLabel,
                        resourceLabel,
                        statusLabel,
                        value_identifier: pack.pack_code,
                        is_default: false,
                        cycle_price: pd.cycle_price || '0',
                        currency: pd.currency || '',
                        cycle_label: pd.cycle_label || 'month',
                        pack_quotas: {
                          cycle_minutes: pd.cycle_minutes || 0,
                          cycle_conversations: pd.cycle_conversations || 0,
                          document_storage_mb: pd.document_storage_mb || 0,
                          max_workflows: pd.max_workflows || 0,
                          max_knowledge_bases: pd.max_knowledge_bases || 0,
                          max_telephony_numbers: pd.max_telephony_numbers || 0,
                        },
                      };
                    })}
                    ariaLabels={{ itemSelectionLabel: (e, item) => `Select ${item.name}` }}
                    cardDefinition={{
                      header: item => (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                            <Icon name="shopping-cart" variant={item.is_default ? 'success' : 'subtle'} size="big" />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 'heading-m', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                              {item.is_default && <Box variant="small" color="text-status-success">Recommended</Box>}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <div style={{ fontSize: 'heading-m', fontWeight: 'bold', color: '#0972d3' }}>
                              {item.cycle_price} {item.currency}
                            </div>
                            <Box variant="small" color="text-status-inactive">/{item.cycle_label || 'month'}</Box>
                          </div>
                        </div>
                      ),
                      sections: [
                        {
                          id: 'stock',
                          header: 'Stock',
                          content: item => {
                            const inStock = item.quotas?.agents?.remaining > 0;
                            return (
                              <Box variant="p">
                                {inStock ? (
                                  <span>
                                    <Icon name="check" variant="success" size="small" />{' '}
                                    <Box variant="strong" color="text-status-success">
                                      {item.quotas?.agents?.remaining} in stock
                                    </Box>
                                  </span>
                                ) : (
                                  <span>
                                    <Icon name="close" variant="error" size="small" />{' '}
                                    <Box variant="strong" color="text-status-error">No stock</Box>
                                  </span>
                                )}
                              </Box>
                            );
                          }
                        },
                        {
                          id: 'resources',
                          header: 'Available resources',
                          content: item => (
                            <Box variant="p">
                              {item.resourceLabel} — <Box variant="strong" color="text-status-info">{item.statusLabel}</Box>
                            </Box>
                          )
                        },
                        {
                          id: 'description',
                          content: item => item.description && (
                            <Box variant="p" color="text-body-secondary">{item.description}</Box>
                          )
                        },
                        {
                          id: 'quotas',
                          header: 'Included quotas',
                          content: item => (
                            <ColumnLayout columns={{ default: 1, s: 2 }} variant="text-grid">
                              <div><Icon name="call" variant="subtle" size="small" /> <Box variant="awsui-key-label">{item.usageLabel}</Box></div>
                              <div><Icon name="file" variant="subtle" size="small" /> <Box variant="awsui-key-label">{item.pack_quotas.document_storage_mb} MB storage</Box></div>
                              <div><Icon name="script" variant="subtle" size="small" /> <Box variant="awsui-key-label">{item.pack_quotas.max_workflows} workflows</Box></div>
                              <div><Icon name="contact" variant="subtle" size="small" /> <Box variant="awsui-key-label">{item.pack_quotas.max_knowledge_bases} knowledge bases</Box></div>
                              <div><Icon name="phone" variant="subtle" size="small" /> <Box variant="awsui-key-label">{item.pack_quotas.max_telephony_numbers} phone numbers</Box></div>
                            </ColumnLayout>
                          )
                        }
                      ]
                    }}
                  />
                </FormField>

                {selectedPack && (
                  <Container header={<Header variant="h3">Pack source</Header>}>
                    <SpaceBetween size="s">
                      <Box variant="p">
                        <strong>{selectedPack.name}</strong>
                      </Box>
                      {selectedPack.id === currentPackId ? (
                        <Alert type="info">This is the current pack associated with the agent. Keeping it allows you to modify other options without changing the pack.</Alert>
                      ) : (
                        <Alert type="success">A new pack from your stock will be assigned to this agent, replacing the current one.</Alert>
                      )}
                    </SpaceBetween>
                  </Container>
                )}
              </SpaceBetween>
            ) : availablePacks.filter(p => p.quotas?.agents?.remaining > 0).length === 0 ? (
              <Alert type="warning" header="No pack in stock">
                Please buy a pack from the <Link href="/packs">Pack Store</Link> before creating an agent.
              </Alert>
            ) : (
              <SpaceBetween size="l">
                <FormField
                  label="Commercial Pack"
                  description="Select the pack that defines this agent's quotas."
                >
                  <Cards
                    selectionType="single"
                    trackBy="id"
                    entireCardClickable
                    cardsPerRow={[{ cards: 1 }, { minWidth: 640, cards: 2 }, { minWidth: 1100, cards: 3 }]}
                    onSelectionChange={({ detail }) => {
                      const pack = detail.selectedItems[0];
                      setSelectedPack(pack);
                      userSelectedPackRef.current = true;
                      setUseStockPack(true);
                      setAutoPurchase(false);
                      console.log('Selected pack:', pack);
                    }}
                    selectedItems={selectedPack ? [selectedPack] : []}
                    items={availablePacks.filter(p => p.quotas?.agents?.remaining > 0).map(pack => {
                      const isVoice = aiAgentType === 'voice';
                      const pd = pack.pack_details || {};
                      const usageLabel = isVoice
                        ? `${pd.cycle_minutes || 0} voice minutes/${pd.cycle_label || 'month'}`
                        : `${pd.cycle_conversations || 0} text conversations/${pd.cycle_label || 'month'}`;
                      const kbTotal = pack.quotas?.knowledge_bases?.total || 0;
                      const kbUsed = pack.quotas?.knowledge_bases?.used || 0;
                      const wfTotal = pack.quotas?.workflows?.total || 0;
                      const wfUsed = pack.quotas?.workflows?.used || 0;
                      const resourceLabel = `KB: ${kbUsed}/${kbTotal} | WF: ${wfUsed}/${wfTotal}`;
                      const statusLabel = pack.status === 'inventory' ? 'In stock' : (pack.has_agent ? 'Assigned' : 'Active');
                      return {
                        ...pack,
                        name: pack.name,
                        description: pack.pack_name,
                        usageLabel,
                        resourceLabel,
                        statusLabel,
                        value_identifier: pack.pack_code,
                        is_default: false,
                        cycle_price: pd.cycle_price || '0',
                        currency: pd.currency || '',
                        cycle_label: pd.cycle_label || 'month',
                        pack_quotas: {
                          cycle_minutes: pd.cycle_minutes || 0,
                          cycle_conversations: pd.cycle_conversations || 0,
                          document_storage_mb: pd.document_storage_mb || 0,
                          max_workflows: pd.max_workflows || 0,
                          max_knowledge_bases: pd.max_knowledge_bases || 0,
                          max_telephony_numbers: pd.max_telephony_numbers || 0,
                        },
                      };
                    })}
                    ariaLabels={{ itemSelectionLabel: (e, item) => `Select ${item.name}` }}
                    cardDefinition={{
                      header: item => (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                            <Icon name="shopping-cart" variant={item.is_default ? 'success' : 'subtle'} size="big" />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 'heading-m', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                              {item.is_default && <Box variant="small" color="text-status-success">Recommended</Box>}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            <div style={{ fontSize: 'heading-m', fontWeight: 'bold', color: '#0972d3' }}>
                              {item.cycle_price} {item.currency}
                            </div>
                            <Box variant="small" color="text-status-inactive">/{item.cycle_label || 'month'}</Box>
                          </div>
                        </div>
                      ),
                      sections: [
                        {
                          id: 'stock',
                          header: 'Stock',
                          content: item => {
                            const inStock = item.quotas?.agents?.remaining > 0;
                            return (
                              <Box variant="p">
                                {inStock ? (
                                  <span>
                                    <Icon name="check" variant="success" size="small" />{' '}
                                    <Box variant="strong" color="text-status-success">
                                      {item.quotas?.agents?.remaining} in stock
                                    </Box>
                                  </span>
                                ) : (
                                  <span>
                                    <Icon name="close" variant="error" size="small" />{' '}
                                    <Box variant="strong" color="text-status-error">No stock</Box>
                                  </span>
                                )}
                              </Box>
                            );
                          }
                        },
                        {
                          id: 'resources',
                          header: 'Available resources',
                          content: item => (
                            <Box variant="p">
                              {item.resourceLabel} — <Box variant="strong" color="text-status-info">{item.statusLabel}</Box>
                            </Box>
                          )
                        },
                        {
                          id: 'description',
                          content: item => item.description && (
                            <Box variant="p" color="text-body-secondary">{item.description}</Box>
                          )
                        },
                        {
                          id: 'quotas',
                          header: 'Included quotas',
                          content: item => (
                            <ColumnLayout columns={{ default: 1, s: 2 }} variant="text-grid">
                              <div><Icon name="call" variant="subtle" size="small" /> <Box variant="awsui-key-label">{item.usageLabel}</Box></div>
                              <div><Icon name="file" variant="subtle" size="small" /> <Box variant="awsui-key-label">{item.pack_quotas.document_storage_mb} MB storage</Box></div>
                              <div><Icon name="script" variant="subtle" size="small" /> <Box variant="awsui-key-label">{item.pack_quotas.max_workflows} workflows</Box></div>
                              <div><Icon name="contact" variant="subtle" size="small" /> <Box variant="awsui-key-label">{item.pack_quotas.max_knowledge_bases} knowledge bases</Box></div>
                              <div><Icon name="phone" variant="subtle" size="small" /> <Box variant="awsui-key-label">{item.pack_quotas.max_telephony_numbers} phone numbers</Box></div>
                            </ColumnLayout>
                          )
                        }
                      ]
                    }}
                  />
                </FormField>

                {selectedPack && (
                  <Container header={<Header variant="h3">Pack source</Header>}>
                    <SpaceBetween size="s">
                      <Box variant="p">
                        <strong>{selectedPack.name}</strong> —{' '}
                        <Box variant="small" color="text-status-success">
                          {selectedPack.quotas?.agents?.remaining} pack(s) in stock
                        </Box>
                      </Box>
                      <SpaceBetween size="xs" direction="horizontal">
                        <Button
                          variant="primary"
                          disabled={selectedPack.quotas?.agents?.remaining === 0}
                          onClick={() => { setUseStockPack(true); setAutoPurchase(false); }}
                        >
                          Use stock pack
                        </Button>
                        <Button
                          variant="normal"
                          href="/packs"
                        >
                          Go to Pack Store
                        </Button>
                      </SpaceBetween>
                      <Alert type="info">An available pack from your stock will be assigned to this agent.</Alert>
                    </SpaceBetween>
                  </Container>
                )}
              </SpaceBetween>
            )}
          </SpaceBetween>
        </Container>
      ),
      disableNavigation: {
        next: !selectedPack || !useStockPack,
        previous: true
      }
    },
    {
      title: "Adexgénie",
      description: "Select your assistant type",
      content: (
        <SpaceBetween direction="vertical" size="l">
          {action === "update" && (
            <Alert type="info">
              You are updating the business type and name for your existing agent.
            </Alert>
          )}
          <FormField
            label="Select Business Type"
            description="Choose the type of business your AI assistant will represent."
          >
          <Cards
            onSelectionChange={({ detail }) =>
              handleAgentTypeSelect(detail.selectedItems[0]) // Pass the whole selected item object
            }
            selectedItems={(() => {
              if (!agentType) return [];
              // Match by id against the loaded businessTypes list
              const matched = businessTypes.find(bt => bt.id === agentType.id);
              return matched ? [matched] : [agentType];
            })()}
            ariaLabels={{
              itemSelectionLabel: (e, n) => `select ${n.name}`,
              selectionGroupLabel: "Agent type selection",
            }}
            trackBy="id"
            entireCardClickable
            cardDefinition={{
              header: (item) => (
                <span style={{ fontSize: 'heading-m', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{item.name}</span>
              ),
              sections: [
                {
                  id: "image",
                  content: item => (
                    item.image_url && <img src={item.image_url} alt={item.name} style={{ maxWidth: '100%', height: '120px', objectFit: 'cover', marginBottom: '2px' }} />
                  )
                },
                {
                  id: "description",
                  header: "Description",
                  content: (item) => item.description,
                },
                {
                  id: "capabilities",
                  header: "Capabilities",
                  content: item => {
                    const capabilities = parseCapabilities(item.capabilities);
                    return capabilities.length > 0 ? (
                      <ul>
                        {capabilities.map((cap, index) => (
                          <li key={index}>{cap}</li>
                        ))}
                      </ul>
                    ) : (
                      <Box variant="small" color="text-body-secondary">No capabilities listed.</Box>
                    );
                  }
                },
              ],
            }}
            cardsPerRow={[{ cards: 1 }, { minWidth: 640, cards: 2 }, { minWidth: 1100, cards: 3 }]} // Responsive card layout
            items={businessTypes} // Use fetched business types
            loading={isLoadingBusinessTypes} // Show loading state only while actively fetching
            loadingText="Loading business types"
            selectionType="single"
            empty={
              <Box textAlign="center" color="inherit">
                <b>{user?.access_token || user?.token || localStorage.getItem("access_token") ? "No business types found" : "Authentication required"}</b>
                <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                  {user?.access_token || user?.token || localStorage.getItem("access_token")
                    ? "Could not load business types from the server."
                    : "Please log in to load business types."}
                </Box>
              </Box>
            }
            header={<Header>Choose Your Business Type</Header>}
          />
          </FormField>

          <FormField
            label="Agent Name"
            description="Enter a name for your AI assistant"
          >
            <Input
              value={agentName}
              onChange={({ detail }) => setAgentName(detail.value)}
              placeholder="Enter a name for your agent"
            />
          </FormField>

          <FormField
            label="Default Language"
            description="Select the default language for your AI assistant."
          >
            <Select
              selectedOption={LANGUAGE_OPTIONS.find(opt => opt.value === (language || "").toLowerCase()) || { value: language, label: language }}
              onChange={({ detail }) => setLanguage(detail.selectedOption.value)}
              options={LANGUAGE_OPTIONS}
            />
          </FormField>

          <FormField
            label="AI Agent Type"
            description={`Voice agents handle calls and chat (voice minutes + text conversations). Text agents handle chat only (text conversations). ${
              voiceAgentAvailable ? "" : "Voice agents are currently disabled."
            }`}
          >
            <Select
              selectedOption={{ value: aiAgentType, label: aiAgentType === 'voice' ? 'Voice' : 'Text Only' }}
              onChange={({ detail }) => setAiAgentType(detail.selectedOption.value)}
              options={[
                {
                  value: "voice",
                  label: voiceAgentAvailable ? "Voice" : "Voice (disabled)",
                  disabled: !voiceAgentAvailable,
                },
                { value: "text", label: "Text Only" }
              ]}
            />
          </FormField>

          <Container header={<Header variant="h4">Brand Information</Header>}>
            <SpaceBetween direction="vertical" size="m">
              <FormField
                label="Brand Name"
                description="The brand this agent represents. Defaults to your company name."
              >
                <Input
                  value={brandName}
                  onChange={({ detail }) => setBrandName(detail.value)}
                  placeholder="e.g., Acme Corp"
                />
              </FormField>
              <FormField
                label="Brand Description"
                description="Short description used in the agent's system prompt."
              >
                <Textarea
                  value={brandDescription}
                  onChange={({ detail }) => setBrandDescription(detail.value)}
                  placeholder="e.g., Acme Corp provides IT consulting and cloud services."
                />
              </FormField>
              <FormField label="Brand Website">
                <Input
                  value={brandWebsite}
                  onChange={({ detail }) => setBrandWebsite(detail.value)}
                  placeholder="https://www.example.com"
                />
              </FormField>
              <FormField label="Business Hours">
                <Input
                  value={businessHours}
                  onChange={({ detail }) => setBusinessHours(detail.value)}
                  placeholder="e.g., Mon-Fri 9h-17h"
                />
              </FormField>
              <FormField label="Brand Contact Email">
                <Input
                  value={brandContactEmail}
                  onChange={({ detail }) => setBrandContactEmail(detail.value)}
                  placeholder="contact@example.com"
                />
              </FormField>
              <FormField label="Brand Contact Phone">
                <Input
                  value={brandContactPhone}
                  onChange={({ detail }) => setBrandContactPhone(detail.value)}
                  placeholder="+213 123 456 789"
                />
              </FormField>
            </SpaceBetween>
          </Container>

          {/* Les champs platform_mode, billing_cycle, currency, temperature, max_tokens sont masqués avec des valeurs par défaut */}
        </SpaceBetween>
      ),
    },
    {
      title: "Services",
      info: <Icon name="tools" variant="success" />,
      description: "Configure your agent's services",
      content: (
        <SpaceBetween direction="vertical" size="l">
          {action === "update" && (
            <Alert type="info">
              You are updating the service configuration for your existing agent.
            </Alert>
          )}
          {agentType && (
            <FormField
              label="Select Service Agent Type"
              description="Choose the specific service agent type for your business. Click a card to select it."
            >
              {!serviceAgentType && (
                <Alert type="warning">
                  Please select a service agent type by clicking on a card below.
                </Alert>
              )}
              <Cards
                onSelectionChange={({ detail }) =>
                  handleServiceAgentTypeSelect(detail.selectedItems[0])
                }
                selectedItems={(() => {
                  const selectedIndex = findSelectedServiceAgentTypeIndex();
                  console.log("[DEBUG] Selected service agent type index:", selectedIndex);
                  
                  if (selectedIndex !== -1) {
                    // Si nous avons trouvé une correspondance, utiliser l'objet exact de la liste
                    return [serviceAgentTypes[selectedIndex]];
                  }
                  
                  // Sinon, utiliser la valeur actuelle (qui pourrait être null)
                  return serviceAgentType ? [serviceAgentType] : [];
                })()}
                ariaLabels={{
                  itemSelectionLabel: (e, n) => `select ${n.name}`,
                  selectionGroupLabel: "Service agent type selection",
                }}
                trackBy="id"
                entireCardClickable
                cardDefinition={{
                  header: (item) => (
                    <span style={{ fontSize: 'heading-m', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{item.name}</span>
                  ),
                  sections: [
                    {
                      id: "image",
                      content: item => (
                        item.image_url && <img src={item.image_url} alt={item.name} style={{ maxWidth: '100%', height: '100px', objectFit: 'cover', marginBottom: '10px' }} />
                      )
                    },
                    {
                      id: "icon",
                      content: item => <Icon name={item.icon || "settings"} size="large" />,
                    },
                    {
                      id: "description",
                      header: "Description",
                      content: (item) => item.description,
                    },
                    {
                      id: "capabilities",
                      header: "Capabilities",
                      content: item => {
                        const capabilities = parseCapabilities(item.capabilities);
                        return capabilities.length > 0 ? (
                          <ul>
                            {capabilities.map((cap, index) => (
                              <li key={index}>{cap}</li>
                            ))}
                          </ul>
                        ) : (
                          <Box variant="small" color="text-body-secondary">No capabilities listed.</Box>
                        );
                      }
                    },
                  ],
                }}
                cardsPerRow={[{ cards: 1 }, { minWidth: 640, cards: 2 }, { minWidth: 1100, cards: 3 }]}
                items={serviceAgentTypes}
                loading={loadingServiceAgentTypes}
                loadingText="Loading service agent types"
                selectionType="single"
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>No service agent types found</b>
                    <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                      No service agent types available for the selected business type.
                    </Box>
                  </Box>
                }
                header={<Header>Choose Your Service Agent Type</Header>}
              />
            </FormField>
          )}
          {/* On-the-fly Workflow Creation */}
          <Container header={<Header variant="h4">Workflows {wizardWorkflows.length > 0 && <Box variant="small" color="text-body-secondary">({wizardWorkflows.length} selected)</Box>}</Header>}>
            <SpaceBetween size="m">
              {/* List of selected workflows */}
              {wizardWorkflows.length > 0 && (
                <SpaceBetween size="xs">
                  {wizardWorkflows.map((wf, idx) => (
                    <div key={wf.id || idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', border: '1px solid #d5dbdb', borderRadius: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon name="script" size="small" />
                        <span><b>{wf.name}</b></span>
                        <Box variant="small" color="text-body-secondary">{wf.service_type || wf.type || 'workflow'}</Box>
                      </div>
                      <Button variant="icon" iconName="remove" onClick={() => {
                        setWizardWorkflows((prev) => prev.filter((w) => w.id !== wf.id));
                      }} title="Remove workflow" />
                    </div>
                  ))}
                </SpaceBetween>
              )}

              {/* Add existing workflow */}
              {existingWorkflows.length > 0 && (
                <FormField label="Add Existing Workflow" description="Select an existing synced workflow to assign to this agent.">
                  <Select
                    selectedOption={selectedExistingWorkflow}
                    onChange={({ detail }) => {
                      setSelectedExistingWorkflow(detail.selectedOption);
                      const wfData = detail.selectedOption.data;
                      if (wfData) {
                        const wfId = wfData.id || wfData.adexcloud_workflow_id;
                        if (!wizardWorkflows.some((w) => (w.id || w.adexcloud_workflow_id) === wfId)) {
                          setWizardWorkflows((prev) => [...prev, wfData]);
                        }
                      }
                      setSelectedExistingWorkflow(null);
                    }}
                    options={existingWorkflows.filter((wf) => {
                      const wfId = wf.value;
                      return !wizardWorkflows.some((w) => (w.id || w.adexcloud_workflow_id) === wfId);
                    })}
                    loading={loadingExistingWorkflows}
                    placeholder="Select an existing workflow to add"
                  />
                </FormField>
              )}
              {existingWorkflows.length === 0 && !loadingExistingWorkflows && wizardWorkflows.length === 0 && (
                <Box color="text-status-info" fontSize="body-s">
                  No existing workflows available. Create a new one below.
                </Box>
              )}
              <Button
                iconName="add-plus"
                onClick={() => setWfModalVisible(true)}
              >
                Create Workflow
              </Button>
            </SpaceBetween>
          </Container>

          <Modal
            visible={wfModalVisible}
            onDismiss={() => setWfModalVisible(false)}
            header="Create Workflow"
            footer={
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setWfModalVisible(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleCreateWorkflowInWizard} loading={resourceSubmitLoading}>
                  Create
                </Button>
              </SpaceBetween>
            }
          >
            <SpaceBetween size="m">
              <FormField label="Name" constraintText="Required">
                <Input value={wfForm.name} onChange={({ detail }) => setWfForm({ ...wfForm, name: detail.value })} />
              </FormField>
              <FormField label="Description">
                <Input value={wfForm.description} onChange={({ detail }) => setWfForm({ ...wfForm, description: detail.value })} />
              </FormField>
              <FormField label="Type">
                <Select
                  selectedOption={{ value: wfForm.workflow_type, label: wfForm.workflow_type === 'mcp' ? 'MCP' : 'Webhook' }}
                  onChange={({ detail }) => setWfForm({ ...wfForm, workflow_type: detail.selectedOption.value })}
                  options={[
                    { value: 'webhook', label: 'Webhook' },
                    { value: 'mcp', label: 'MCP' },
                  ]}
                />
              </FormField>
              <FormField label={wfForm.workflow_type === 'mcp' ? 'MCP Server URL' : 'Webhook URL'}>
                <Input value={wfForm.url} onChange={({ detail }) => setWfForm({ ...wfForm, url: detail.value })} placeholder={wfForm.workflow_type === 'mcp' ? 'https://sms.adexgenie.ai/mcp' : 'https://api.example.com/webhook'} />
              </FormField>
              <FormField label="API Key (Authorization header)" description="Optional. Sent in request headers or MCP config.">
                <Input value={wfForm.api_key} onChange={({ detail }) => setWfForm({ ...wfForm, api_key: detail.value })} placeholder="Bearer sk-... or your-api-key" type="password" />
              </FormField>
              <FormField label="Action">
                <Input value={wfForm.action} onChange={({ detail }) => setWfForm({ ...wfForm, action: detail.value })} placeholder="send_sms" />
              </FormField>
              <FormField label="Parameters">
                <SpaceBetween size="s">
                  {wfForm.params.map((p, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                      <div style={{ flex: 2 }}>
                        <FormField label={idx === 0 ? "Key" : null}>
                          <Input value={p.key} onChange={({ detail }) => {
                            const next = [...wfForm.params];
                            next[idx].key = detail.value;
                            setWfForm({ ...wfForm, params: next });
                          }} placeholder="to" />
                        </FormField>
                      </div>
                      <div style={{ flex: 2 }}>
                        <FormField label={idx === 0 ? "Type" : null}>
                          <Select
                            selectedOption={{ value: p.type, label: p.type }}
                            onChange={({ detail }) => {
                              const next = [...wfForm.params];
                              next[idx].type = detail.selectedOption.value;
                              setWfForm({ ...wfForm, params: next });
                            }}
                            options={[
                              { value: 'string', label: 'String' },
                              { value: 'number', label: 'Number' },
                              { value: 'boolean', label: 'Boolean' },
                            ]}
                          />
                        </FormField>
                      </div>
                      <div style={{ flex: 3 }}>
                        <FormField label={idx === 0 ? "Default value" : null}>
                          <Input value={p.defaultValue} onChange={({ detail }) => {
                            const next = [...wfForm.params];
                            next[idx].defaultValue = detail.value;
                            setWfForm({ ...wfForm, params: next });
                          }} placeholder="{{forward_number}}" />
                        </FormField>
                      </div>
                      <div>
                        <Button variant="icon" iconName="remove" onClick={() => setWfForm({ ...wfForm, params: wfForm.params.filter((_, i) => i !== idx) })} ariaLabel="Remove parameter" />
                      </div>
                    </div>
                  ))}
                  <Button variant="inline-icon" iconName="add-plus" onClick={() => setWfForm({ ...wfForm, params: [...wfForm.params, { key: '', type: 'string', defaultValue: '' }] })}>Add parameter</Button>
                </SpaceBetween>
              </FormField>
            </SpaceBetween>
          </Modal>
        </SpaceBetween>
      ),
    },
    {
      title: "Phone Number",
      info: <Icon name="phone" variant="success" />,
      description: "Set up your agent's phone",
      content: (
        <SpaceBetween size="l">
          {action === "update" && (
            <Alert type="info">
              You are updating the phone number configuration for your existing agent.
            </Alert>
          )}
          <Container header={<Header variant="h3"><Icon name="phone" size="medium" /> Phone Number Configuration</Header>}>
            <SpaceBetween size="m">
              <Alert type="info">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Icon name="info" size="big" variant="subtle" />
                  <span style={{ marginLeft: '10px' }}>Your agent needs a dedicated phone number to receive calls.</span>
                </div>
              </Alert>
              <Box variant="p">
                Click the button below to request a phone number for your AI agent.
              </Box>
              
              <ExpandableSection headerText="SIP Configuration (optional)">
                <SpaceBetween size="m">
                  <FormField label="Trunk Name">
                    <Input value={wizardSipPayload.trunk_name} onChange={({ detail }) => setWizardSipPayload({ ...wizardSipPayload, trunk_name: detail.value })} placeholder="Inbound Medical Trunk" />
                  </FormField>
                  <FormField label="Phone Numbers (comma-separated)">
                    <Input value={wizardSipPayload.phone_numbers} onChange={({ detail }) => setWizardSipPayload({ ...wizardSipPayload, phone_numbers: detail.value })} placeholder="+33123456789, +33698765432" />
                  </FormField>
                  <FormField label="Allowed Addresses (comma-separated)">
                    <Input value={wizardSipPayload.allowed_addresses} onChange={({ detail }) => setWizardSipPayload({ ...wizardSipPayload, allowed_addresses: detail.value })} placeholder="sip:provider1.com" />
                  </FormField>
                  <FormField label="Allowed IPs (comma-separated)">
                    <Input value={wizardSipPayload.allowed_ips} onChange={({ detail }) => setWizardSipPayload({ ...wizardSipPayload, allowed_ips: detail.value })} placeholder="203.0.113.0/24" />
                  </FormField>
                </SpaceBetween>
              </ExpandableSection>

              {!twilioProvisionedNumber ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                  <Button
                    onClick={async () => {
                      try {
                        setIsCallLoading(true);
                        const sipPayload = {};
                        if (wizardSipPayload.trunk_name.trim()) sipPayload.trunk_name = wizardSipPayload.trunk_name.trim();
                        if (wizardSipPayload.phone_numbers.trim()) sipPayload.phone_numbers = wizardSipPayload.phone_numbers.split(',').map(s => s.trim()).filter(Boolean);
                        if (wizardSipPayload.allowed_addresses.trim()) sipPayload.allowed_addresses = wizardSipPayload.allowed_addresses.split(',').map(s => s.trim()).filter(Boolean);
                        if (wizardSipPayload.allowed_ips.trim()) sipPayload.allowed_ips = wizardSipPayload.allowed_ips.split(',').map(s => s.trim()).filter(Boolean);
                        const result = await handleNextStep("Phone Number", Object.keys(sipPayload).length > 0 ? { sip_payload: sipPayload } : {});
                        if (result) {
                          setStepStatus((prev) => ({ ...prev, "Phone Number": "success" }));
                        }
                      } catch (error) {
                        console.error("Error getting phone number:", error);
                      } finally {
                        setIsCallLoading(false);
                      }
                    }}
                    variant="primary"
                    disabled={isCallLoading}
                    iconName="phone"
                  >
                    {isCallLoading ? <Spinner /> : "Get Phone Number"}
                  </Button>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <StatusIndicator type="success">
                    Phone number acquired successfully!
                  </StatusIndicator>
                  <Box variant="h4" padding={{ top: "m" }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon name="phone" size="big" variant="success" />
                      <span style={{ marginLeft: '10px' }}>{twilioProvisionedNumber}</span>
                    </div>
                  </Box>
                </div>
              )}
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      ),
      disableNavigation: {
        next: !twilioProvisionedNumber,
        previous: true
      }
    },
    {
      title: "Files",
      info: <Icon name="file" variant="success" />,
      description: "Upload knowledge base files",
      content: (
        <Form>
          <Container header={<Header variant="h3"><Icon name="file" size="medium" /> Knowledge Base Files</Header>}>
            <SpaceBetween size="l">
              <Alert type="info">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Icon name="info" size="big" variant="subtle" />
                  <span style={{ marginLeft: '10px' }}>Select or create a knowledge base first, then upload files to train your agent.</span>
                </div>
              </Alert>

              {/* Knowledge Base selection / creation — must be done BEFORE uploading files */}
              <Container header={<Header variant="h4">Knowledge Bases {wizardKbs.length > 0 && <Box variant="small" color="text-body-secondary">({wizardKbs.length} selected)</Box>}</Header>}>
                <SpaceBetween size="m">
                  {/* List of selected KBs */}
                  {wizardKbs.length > 0 && (
                    <SpaceBetween size="xs">
                      {wizardKbs.map((kb, idx) => (
                        <div key={kb.id || kb.adexcloud_kb_id || idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', border: '1px solid #d5dbdb', borderRadius: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Icon name="file" size="small" />
                            <span><b>{kb.name}</b></span>
                            {wizardKb?.id === kb.id && <StatusIndicator type="success">Active for upload</StatusIndicator>}
                          </div>
                          <SpaceBetween direction="horizontal" size="xs">
                            {wizardKb?.id !== kb.id && (
                              <Button variant="link" onClick={() => { setWizardKb(kb); setFileskn([]); setFileStatuses({}); setFileidx([]); }}>
                                Set active
                              </Button>
                            )}
                            <Button variant="icon" iconName="remove" onClick={() => {
                              setWizardKbs((prev) => prev.filter((k) => (k.id || k.adexcloud_kb_id) !== (kb.id || kb.adexcloud_kb_id)));
                              if (wizardKb?.id === kb.id) {
                                setWizardKb(null);
                                setFileskn([]);
                                setFileStatuses({});
                                setFileidx([]);
                              }
                            }} title="Remove KB" />
                          </SpaceBetween>
                        </div>
                      ))}
                    </SpaceBetween>
                  )}

                  {/* Add KB section */}
                  <SpaceBetween size="m">
                    {existingKbs.length > 0 && (
                      <FormField label="Add Existing Knowledge Base" description="Select an existing synced KB to assign to this agent.">
                        <Select
                          selectedOption={selectedExistingKb}
                          onChange={({ detail }) => {
                            setSelectedExistingKb(detail.selectedOption);
                            const kbData = detail.selectedOption.data;
                            if (kbData) {
                              const kbId = kbData.id || kbData.adexcloud_kb_id;
                              if (!wizardKbs.some((k) => (k.id || k.adexcloud_kb_id) === kbId)) {
                                setWizardKbs((prev) => [...prev, kbData]);
                                if (!wizardKb) {
                                  setWizardKb(kbData);
                                }
                              }
                            }
                            setSelectedExistingKb(null);
                          }}
                          options={existingKbs.filter((kb) => {
                            const kbId = kb.value;
                            return !wizardKbs.some((k) => (k.id || k.adexcloud_kb_id) === kbId);
                          })}
                          loading={loadingExistingKbs}
                          placeholder="Select an existing knowledge base to add"
                        />
                      </FormField>
                    )}
                    {existingKbs.length === 0 && !loadingExistingKbs && wizardKbs.length === 0 && (
                      <Box color="text-status-info" fontSize="body-s">
                        No existing knowledge bases available. Create a new one below.
                      </Box>
                    )}
                    <Button
                      iconName="add-plus"
                      onClick={() => setKbModalVisible(true)}
                    >
                      Create New Knowledge Base
                    </Button>
                  </SpaceBetween>
                </SpaceBetween>
              </Container>

              {/* File upload — only shown when a KB is selected */}
              <FormField
                label={<div style={{ display: 'flex', alignItems: 'center' }}><Icon name="upload" size="small" /> Upload Knowledge Base Files</div>}
                description={wizardKb ? "Upload files to the selected knowledge base." : "Select or create a knowledge base above to enable file upload."}
              >
                {wizardKb ? (
                  <FileUpload
                    onChange={({ detail }) => {
                      console.log("Files selected:", detail.value);
                      setFileskn(detail.value);
                    }}
                    value={fileskn}
                    i18nStrings={{
                      uploadButtonText: (e) =>
                        e ? "Choose files" : "Choose files",
                      dropzoneText: (e) =>
                        "Drop files to upload",
                      removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                      limitShowFewer: "show less files",
                      limitShowMore: "show more files",
                      errorIconAriaLabel: "Error",
                      warningIconAriaLabel: "Warning",
                    }}
                    multiple
                    showFileLastModified
                    showFileSize
                    showFileThumbnail
                    tokenLimit={3}
                    constraintText="Supported formats: .pdf, .txt, .docx (Max 10MB)"
                    tokenStatus={fileskn.reduce(
                      (acc, file) => ({
                        ...acc,
                        [file.name]: getTokenStatus(file),
                      }),
                      {}
                    )}
                  />
                ) : (
                  <div style={{
                    padding: '20px',
                    border: '2px dashed #d5dbdb',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#687078',
                    backgroundColor: '#f7f8f8',
                  }}>
                    <Icon name="upload" size="medium" variant="subtle" />
                    <div style={{ marginTop: '8px' }}>Select or create a knowledge base above to enable file upload</div>
                  </div>
                )}
              </FormField>
              {fileskn.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                  <Alert type="info">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Icon name="status-info" size="small" />
                      <span style={{ marginLeft: '5px' }}>Files selected! Click the button below to upload them to your knowledge base.</span>
                    </div>
                  </Alert>
                  <Button
                    onClick={() => {
                      console.log("Upload button clicked");
                      setIsLoading(true);

                      handleUploadFiles()
                        .then(result => {
                          console.log("Upload completed:", result);
                        })
                        .catch(err => {
                          console.error("Upload error:", err);
                        })
                        .finally(() => {
                          setIsLoading(false);
                        });
                    }} 
                    disabled={isLoading || !wizardKb} 
                    iconName="upload" 
                    variant="primary"
                  >
                    {isLoading ? <Spinner /> : "Add to Knowledge Base"}
                  </Button>
                  {error && (
                    <Alert type="error">
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Icon name="status-negative" size="small" />
                        <span style={{ marginLeft: '5px' }}>Error: {error}</span>
                      </div>
                    </Alert>
                  )}
                </div>
              )}

              {/* Live processing status — keep visible after upload so long-running processing is still shown */}
              {Object.keys(fileStatuses).length > 0 && (
                <div style={{ padding: '12px', backgroundColor: '#f7f8f8', borderRadius: '8px', border: '1px solid #d5dbdb', marginTop: '10px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Processing status:</div>
                  {Object.entries(fileStatuses).map(([name, s]) => {
                    const proc = s.processing;
                    const steps = [];
                    if (proc) {
                      if (proc.downloading !== undefined) steps.push({ label: 'Download', done: proc.downloading === true || proc.downloading === 'done' });
                      if (proc.extracting !== undefined) steps.push({ label: 'Extract', done: proc.extracting === true || proc.extracting === 'done' });
                      if (proc.chunking !== undefined) steps.push({ label: 'Chunk', done: proc.chunking === true || proc.chunking === 'done' });
                      if (proc.embedding !== undefined) steps.push({ label: 'Embed', done: proc.embedding === true || proc.embedding === 'done' });
                      if (proc.indexing !== undefined) steps.push({ label: 'Index', done: proc.indexing === true || proc.indexing === 'done' });
                      if (proc.uploading !== undefined) steps.push({ label: 'Upload', done: proc.uploading === true || proc.uploading === 'done' });
                    }
                    return (
                      <div key={name} style={{ marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {s.status === 'success' || s.status === 'completed' ? (
                            <Icon name="check" size="small" variant="success" />
                          ) : s.status === 'error' || s.status === 'failed' ? (
                            <Icon name="status-negative" size="small" variant="error" />
                          ) : (
                            <Spinner size="small" />
                          )}
                          <span>{name}</span>
                          <span style={{ fontSize: '12px', color: '#687078', textTransform: 'capitalize' }}>({s.status})</span>
                        </div>
                        {s.status !== 'success' && s.status !== 'completed' && steps.length > 0 && (
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginLeft: '20px', marginTop: '4px' }}>
                            {steps.map((st) => (
                              <span key={st.label} style={{
                                fontSize: '11px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                backgroundColor: st.done ? '#d8f0d8' : '#e8e8e8',
                                color: st.done ? '#1a7a1a' : '#666',
                              }}>
                                {st.done ? '✓' : '…'} {st.label}
                              </span>
                            ))}
                          </div>
                        )}
                        {s.error && (
                          <div style={{ fontSize: '12px', color: '#d9152b', marginLeft: '20px', marginTop: '2px' }}>{s.error}</div>
                        )}
                        {s.cost_analysis && (
                          <div style={{ fontSize: '11px', color: '#687078', marginLeft: '20px', marginTop: '2px' }}>
                            {s.cost_analysis.tokens ? `${s.cost_analysis.tokens} tokens` : ''}
                            {s.cost_analysis.tokens && s.cost_analysis.cost ? ' · ' : ''}
                            {s.cost_analysis.cost ? `${s.cost_analysis.cost} ${s.cost_analysis.currency || ''}` : ''}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Display success or error message based on actual file statuses */}
              {Object.keys(fileStatuses).length > 0 && (
                <>
                  {Object.values(fileStatuses).every((s) => s.status === 'success' || s.status === 'completed') ? (
                    <div style={{ padding: '15px', backgroundColor: '#f2fcf3', borderRadius: '8px', border: '1px solid #d0f0d5' }}>
                      <StatusIndicator type="success">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Icon name="check" size="small" variant="success" />
                          <span style={{ marginLeft: '5px' }}>Files uploaded and processed successfully! You can now proceed to the next step.</span>
                        </div>
                      </StatusIndicator>
                    </div>
                  ) : Object.values(fileStatuses).some((s) => s.status === 'error' || s.status === 'failed') ? (
                    <div style={{ padding: '15px', backgroundColor: '#fff7f7', borderRadius: '8px', border: '1px solid #f2b0b0' }}>
                      <StatusIndicator type="error">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Icon name="status-negative" size="small" />
                          <span style={{ marginLeft: '5px' }}>
                            Some files failed to process on adexcloud.
                            {Object.entries(fileStatuses)
                              .filter(([, s]) => s.status === 'error' || s.status === 'failed')
                              .map(([name, s]) => (
                                <div key={name} style={{ marginTop: '4px', fontSize: '12px' }}>
                                  • {name}: {s.error || 'Processing failed'}
                                </div>
                              ))}
                          </span>
                        </div>
                      </StatusIndicator>
                    </div>
                  ) : Object.values(fileStatuses).some((s) => s.status && !['success', 'completed', 'error', 'failed'].includes(s.status)) ? (
                    <div style={{ padding: '15px', backgroundColor: '#f0f7ff', borderRadius: '8px', border: '1px solid #b8d4f0' }}>
                      <StatusIndicator type="info">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <Icon name="status-info" size="small" />
                          <span style={{ marginLeft: '5px' }}>
                            Some files are still being processed on adexcloud. This can take a while. You can continue using the wizard and check the knowledge base status later.
                          </span>
                        </div>
                      </StatusIndicator>
                    </div>
                  ) : null}
                </>
              )}

              <Modal
                visible={kbModalVisible}
                onDismiss={() => setKbModalVisible(false)}
                header="Create Knowledge Base"
                footer={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={() => setKbModalVisible(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleCreateKbInWizard} loading={resourceSubmitLoading}>
                      Create
                    </Button>
                  </SpaceBetween>
                }
              >
                <SpaceBetween size="m">
                  <FormField label="Name" constraintText="Required">
                    <Input value={kbForm.name} onChange={({ detail }) => setKbForm({ ...kbForm, name: detail.value })} />
                  </FormField>
                  <FormField label="Description">
                    <Input value={kbForm.description} onChange={({ detail }) => setKbForm({ ...kbForm, description: detail.value })} />
                  </FormField>
                </SpaceBetween>
              </Modal>
            </SpaceBetween>
          </Container>
        </Form>
      ),
      isOptional: true,
      disableNavigation: {
        next: (() => {
          const fileStatusValues = Object.values(fileStatuses);
          const hasUploadingFiles = fileskn.length > 0 && fileStatusValues.length === 0;
          const hasPendingFiles = fileStatusValues.some(
            (s) => s.status && !['success', 'completed', 'error', 'failed'].includes(s.status)
          );
          const hasFailedFiles = fileStatusValues.some(
            (s) => s.status === 'error' || s.status === 'failed'
          );
          return hasUploadingFiles || hasPendingFiles || hasFailedFiles;
        })(),
        previous: true
      }
    },
    {
      title: "Deployability Check",
      info: <Icon name="status-info" variant="info" />,
      description: "Verify the agent can really be deployed",
      content: (
        <Container header={<Header variant="h3"><Icon name="status-info" size="medium" /> Deployability Check</Header>}>
          <SpaceBetween size="m">
            <Alert type="info">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Icon name="info" size="big" variant="subtle" />
                <span style={{ marginLeft: '10px' }}>
                  Verifying that the selected pack has enough quota for this agent.
                </span>
              </div>
            </Alert>

            {deployabilityLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <StatusIndicator type="in-progress">
                  <span style={{ marginLeft: '10px' }}>Checking deployability...</span>
                </StatusIndicator>
              </div>
            )}

            {deployabilityResult && (
              <Alert
                type={deployabilityResult.deployable ? "success" : "error"}
                header={deployabilityResult.deployable ? "Agent is deployable" : "Agent cannot be deployed"}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Icon
                    name={deployabilityResult.deployable ? "status-positive" : "status-negative"}
                    size="big"
                    variant={deployabilityResult.deployable ? "success" : "error"}
                  />
                  <span style={{ marginLeft: '10px' }}>
                    {deployabilityResult.message}
                  </span>
                </div>
                {deployabilityResult.details && (
                  <Box variant="pre" padding={{ top: 's' }}>
                    {JSON.stringify(deployabilityResult.details, null, 2)}
                  </Box>
                )}
              </Alert>
            )}
          </SpaceBetween>
        </Container>
      ),
      disableNavigation: {
        next: !deployabilityResult?.deployable
      }
    },
    {
      title: "Congratulations!",
      info: <Icon name="check-circle" variant="success" />,
      description: "Your agent is ready",
      content: (
        <Container header={<Header variant="h3"><Icon name="check-circle" size="big" variant="success" /> Congratulations! Your Agent is Ready</Header>}>
          <SpaceBetween size="l">
            {/* Success banner */}
            <Alert
              type="success"
              header="Your assistant has been successfully deployed!"
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Icon name="status-positive" size="big" />
                <span style={{ marginLeft: '10px' }}>Your AI agent is now ready to handle calls and interactions with your customers.</span>
              </div>
            </Alert>
            
            {/* Agent information card */}
            <Container
              header={<Header variant="h4"><Icon name="information" size="small" /> Your Agent Details</Header>}
            >
              <ColumnLayout columns={{ default: 1, s: 1, m: 2, l: 2, xl: 2 }} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Agent Name</Box>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Icon name="robot" size="small" />
                    <span style={{ marginLeft: '5px' }}>{agentName}</span>
                  </div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Agent Type</Box>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Icon name={aiAgentType === 'voice' ? 'call' : 'chat'} size="small" />
                    <span style={{ marginLeft: '5px' }}>{aiAgentType === 'voice' ? 'Voice' : 'Text Only'}</span>
                  </div>
                </div>
                {aiAgentType === 'voice' && (
                  <div>
                    <Box variant="awsui-key-label">Dedicated Phone Number</Box>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Icon name="phone" size="small" />
                      <span style={{ marginLeft: '5px' }}>{twilioProvisionedNumber}</span>
                    </div>
                  </div>
                )}
                <div>
                  <Box variant="awsui-key-label">Status</Box>
                  <StatusIndicator type="success">Active</StatusIndicator>
                </div>
                <div>
                  <Box variant="awsui-key-label">Assistant ID</Box>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Icon name="key" size="small" />
                    <span style={{ marginLeft: '5px' }}>{agentExternalId || "N/A"}</span>
                  </div>
                </div>
              </ColumnLayout>
            </Container>

            {/* Widget code section */}
            {widgetCode && (
              <Container
                header={<Header variant="h4"><Icon name="copy" size="small" /> Widget Code</Header>}
              >
                <SpaceBetween size="m">
                  <Box variant="p">
                    Copy and paste this code snippet into your website to embed the AdexGenie widget.
                  </Box>
                  <div style={{ position: 'relative' }}>
                    <pre
                      style={{
                        background: '#f4f4f4',
                        padding: '16px',
                        borderRadius: '8px',
                        overflow: 'auto',
                        fontSize: '12px',
                        maxHeight: '200px',
                      }}
                    >
                      <code>{widgetCode}</code>
                    </pre>
                    <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                      <Button
                        iconName="copy"
                        onClick={() => copyToClipboard(widgetCode)}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                </SpaceBetween>
              </Container>
            )}

            {/* Try your agent section */}
            {aiAgentType === 'voice' ? (
              <Container
                header={<Header variant="h4"><Icon name="phone" size="small" /> Try Your Agent</Header>}
              >
                <SpaceBetween size="m">
                  <Box variant="p">
                    Test your agent by making a call directly from here. This will help you verify that everything is working correctly.
                  </Box>

                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                    {callStatus === "idle" ? (
                      <Button
                        variant="primary"
                        onClick={startAgentCall}
                        disabled={!agentExternalId || isCallLoading}
                        iconName="call"
                      >
                        {isCallLoading ? <Spinner /> : "Start Test Call"}
                      </Button>
                    ) : (
                      <Button
                        variant="critical"
                        onClick={stopAgentCall}
                        iconName="close"
                      >
                        End Call
                      </Button>
                    )}
                  </div>

                  {callStatus === "active" && (
                    <div style={{ textAlign: 'center', padding: '15px' }}>
                      <StatusIndicator type="in-progress">
                        Call in progress
                      </StatusIndicator>
                      <div style={{ marginTop: '15px' }}>
                        {generateWaveBars(volumeLevel)}
                      </div>
                    </div>
                  )}

                  {callStatus === "error" && (
                    <Alert type="error" header="Call Error">
                      There was a problem connecting to the agent. Please try again later.
                    </Alert>
                  )}
                </SpaceBetween>
              </Container>
            ) : (
              <Container
                header={<Header variant="h4"><Icon name="chat" size="small" /> Try Your Agent</Header>}
              >
                <SpaceBetween size="m">
                  <Box variant="p">
                    Your text agent is ready. You can integrate it into your chat widget or use the API to start conversations.
                  </Box>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                    <Button
                      variant="primary"
                      disabled={true}
                      iconName="chat"
                    >
                      Chat widget coming soon
                    </Button>
                  </div>
                </SpaceBetween>
              </Container>
            )}
            
            {/* Footer buttons */}
            <Box textAlign="center" padding={{ top: "l", bottom: "s" }}>
              <SpaceBetween size="l" direction="horizontal">
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        </Container>
      ),
    },
    /* eslint-disable react-hooks/exhaustive-deps */
  ].filter(step => aiAgentType !== 'text' || step.title !== 'Phone Number'), [
    businessTypes,
    agentType,
    agentName,
    brandName,
    brandDescription,
    brandWebsite,
    businessHours,
    brandContactEmail,
    brandContactPhone,
    twilioProvisionedNumber,
    serviceAgentTypes,
    serviceAgentType,
    loadingServiceAgentTypes,
    fileskn,
    fileStatuses,
    isDeploying,
    agentExternalId,
    widgetCode,
    copyToClipboard,
    callStatus,
    isCallLoading,
    handleAgentTypeSelect,
    handleServiceAgentTypeSelect,
    handleUploadFiles,
    startAgentCall,
    stopAgentCall,
    generateWaveBars,
    volumeLevel,
    getTokenStatus,
    isLoading,
    services,
    language,
    gender,
    aiAgentType,
    platformMode,
    billingCycle,
    currency,
    temperature,
    maxTokens,
    availablePacks,
    selectedPack,
    loadingPacks,
    deployabilityLoading,
    deployabilityResult,
    checkDeployability,
    wizardWorkflows,
    wizardKbs,
    wizardKb,
    existingWorkflows,
    existingKbs,
    selectedExistingWorkflow,
    selectedExistingKb,
    loadingExistingWorkflows,
    loadingExistingKbs,
    fileskn,
    fileStatuses,
    action,
    wfModalVisible,
    kbModalVisible,
    resourceSubmitLoading
    /* eslint-enable react-hooks/exhaustive-deps */
  ]);

  // Automatically run the deployability check when the user reaches the Deployability Check step
  useEffect(() => {
    const currentStepTitle = steps[activeStepIndex]?.title;
    if (
      currentStepTitle === "Deployability Check" &&
      selectedPack &&
      !deployabilityLoading &&
      deployabilityResult === null
    ) {
      checkDeployability();
    }
  }, [activeStepIndex, steps, selectedPack, deployabilityLoading, deployabilityResult, checkDeployability]);

  // Safety: if the backend marks the Agent Deployment step as success but the wizard
  // didn't advance (e.g. stale closure), explicitly move to the Congratulations step.
  useEffect(() => {
    const currentStepTitle = steps[activeStepIndex]?.title;
    if (
      stepStatus["Agent Deployment"] === "success" &&
      activeStepIndex < steps.length - 1 &&
      currentStepTitle === "Deployability Check"
    ) {
      const congratsIndex = steps.findIndex((s) => s.title === "Congratulations!");
      if (congratsIndex !== -1 && congratsIndex !== activeStepIndex) {
        console.log("Auto-advancing to Congratulations! step", { congratsIndex });
        setActiveStepIndex(congratsIndex);
      }
    }
  }, [stepStatus, activeStepIndex, steps]);

  const handleNextStep = useCallback(
    async (stepTitle, stepData) => {
      // Prevent double POST for Agent Deployment step
      if (stepTitle === "Agent Deployment" && deploymentInProgressRef.current) {
        console.log("[DEBUG] Agent Deployment already in progress, skipping duplicate POST");
        return true;
      }
      if (stepTitle === "Agent Deployment") {
        deploymentInProgressRef.current = true;
      }
      setIsNextLoading(true); // Start loading
      try {
        const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
        if (!accessToken) {
          console.error("No access token available");
          notifyError("Authentication error. Please log in again.");
          return false;
        }
        
        // Format step data correctly before sending
        let formattedData = { ...stepData };
        
        // For debugging
        console.log(`Sending data for ${stepTitle}:`, formattedData);
        console.log(`Action: ${action}, Creation ID: ${creationId || 'none'}`);
        
        const response = await axios.post(
          `/api/deploy-assistant`,
          {
            step_name: stepTitle,
            data: formattedData,
            action: action,                  // Use the action state variable
            creation_id: creationIdRef.current // Always use the latest creation ID from the ref
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.status === "success" || response.data.status === "pending") {
          setStepStatus((prev) => ({ ...prev, [stepTitle]: "success" }));

          // Store the creation ID for future requests
          if (response.data.creation_id) {
            setCreationId(response.data.creation_id);
            creationIdRef.current = response.data.creation_id;
          }

          // Handle specific step responses
          if (stepTitle === "Phone Number") {
            setTwilioProvisionedNumber(response.data.phoneNumber);
          }
          if (stepTitle === "Agent Deployment") {
            const externalId = response.data.adexcloud_agent_id || response.data.assistantId;
            setAgentExternalId(externalId);
            // If the backend says deployment is pending, poll with POST to trigger phase 2 (deploy)
            if (response.data.status === "pending") {
              notifySuccess(response.data.message || "Agent deployment is in progress. Please wait...");
              const cid = response.data.creation_id || creationIdRef.current;
              // Poll up to 12 times (every 5s = 60s max)
              for (let i = 0; i < 12; i++) {
                await new Promise((resolve) => setTimeout(resolve, 5000));
                // Re-POST to trigger phase 2 (deploy) on the backend
                try {
                  const pollResp = await axios.post(
                    `/api/deploy-assistant`,
                    {
                      step_name: "Agent Deployment",
                      data: { company_pack_id: selectedPack?.id },
                      action: action,
                      creation_id: cid
                    },
                    { headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" } }
                  );
                  if (pollResp.data.status === "success") {
                    console.log("[DEBUG] Agent Deployment completed via phase 2 POST");
                    setStepStatus((prev) => ({ ...prev, "Agent Deployment": "success" }));
                    if (pollResp.data.adexcloud_agent_id) setAgentExternalId(pollResp.data.adexcloud_agent_id);
                    if (pollResp.data.creation_id) { setCreationId(pollResp.data.creation_id); creationIdRef.current = pollResp.data.creation_id; }
                    break;
                  }
                  if (pollResp.data.status === "error") {
                    console.log("[DEBUG] Agent Deployment failed via phase 2 POST");
                    setStepStatus((prev) => ({ ...prev, "Agent Deployment": "failed" }));
                    notifyError(pollResp.data.message || "Agent deployment failed.");
                    return false;
                  }
                  // Still pending — continue polling
                } catch (pollErr) {
                  // 202 pending is expected — check via fetchWizardStatus as fallback
                  await fetchWizardStatus(cid);
                  const currentStepStatus = stepStatusRef.current["Agent Deployment"];
                  if (currentStepStatus === "completed" || currentStepStatus === "success") {
                    console.log("[DEBUG] Agent Deployment completed via fetchWizardStatus");
                    setStepStatus((prev) => ({ ...prev, "Agent Deployment": "success" }));
                    break;
                  }
                  if (currentStepStatus === "failed") {
                    console.log("[DEBUG] Agent Deployment failed via fetchWizardStatus");
                    setStepStatus((prev) => ({ ...prev, "Agent Deployment": "failed" }));
                    notifyError("Agent deployment failed. Please try again.");
                    return false;
                  }
                }
              }
            }
            // Fetch widget code for the newly created agent
            if (externalId) {
              try {
                const widgetRes = await axios.get(
                  `/api/agents/${externalId}/widget-code/`,
                  { headers: { Authorization: `Bearer ${accessToken}` } }
                );
                if (widgetRes.data?.widget_code) {
                  setWidgetCode(widgetRes.data.widget_code);
                }
              } catch (widgetErr) {
                console.error('Failed to fetch widget code:', widgetErr);
              }
            }
          }
          return true; // Success
        } else {
          setStepStatus((prev) => ({ ...prev, [stepTitle]: "failed" }));
          // Use a more user-friendly error display instead of alert
          console.error(`Error: ${response.data.message}`);
          return false; // Failure
        }
      } catch (error) {
        console.error("Step execution error:", error);
        console.error("Error response:", error.response?.data);
        
        setStepStatus((prev) => ({ ...prev, [stepTitle]: "failed" }));
        
        // Handle specific error codes with user-friendly messages
        if (error.response?.status === 401) {
          console.error("Your session has expired. Please log in again.");
          notifyError("Your session has expired. Please log in again.");
        } else if (error.response?.status === 403 && error.response?.data?.message) {
          console.error(`Forbidden: ${error.response.data.message}`);
          notifyError(error.response.data.message);
        } else if (error.response?.status === 400 && error.response?.data?.message) {
          console.error(`Validation error: ${error.response.data.message}`);
          
          // Afficher un message d'erreur spécifique pour le champ manquant
          if (error.response.data.field === "agentName") {
            notifyError(`Le nom de l'agent est obligatoire.`);
          } else {
            notifyError(error.response.data.message);
          }
        } else if (error.response?.status === 500 && error.response?.data?.message) {
          console.error(`Server error: ${error.response.data.message}`);
          notifyError(`Erreur serveur: ${error.response.data.message}`);
        } else {
          console.error("Error during step execution. Please try again.");
          notifyError("Une erreur s'est produite. Veuillez réessayer.");
        }
        
        return false; // Failure
      } finally {
        if (stepTitle === "Agent Deployment") {
          deploymentInProgressRef.current = false;
        }
        setIsNextLoading(false); // End loading
      }
    },
    [user, creationId, action, notifyError, notifySuccess, fetchWizardStatus, selectedPack]  // Include action, creationId, and selectedPack in dependencies
  );

  const handleNavigate = useCallback(
    async ({ detail }) => {
      const { requestedStepIndex } = detail;

      // Get the current step title
      const currentStep = steps[activeStepIndex].title;

      // Allow going back without any checks
      if (requestedStepIndex < activeStepIndex) {
        setActiveStepIndex(requestedStepIndex);
        return;
      }

      // Block forward navigation from Pack step if no pack with stock is selected (create mode only)
      if (currentStep === "Pack" && action !== "update") {
        if (loadingPacks) {
          notifyError("Veuillez attendre le chargement des packs.");
          return;
        }
        const packsInStock = availablePacks.filter(p => p.quotas?.agents?.remaining > 0);
        if (packsInStock.length === 0) {
          notifyError("Aucun pack disponible en stock. Veuillez acheter un pack avant de continuer.");
          return;
        }
        if (!selectedPack || selectedPack.quotas?.agents?.remaining === 0) {
          notifyError("Veuillez sélectionner un pack avec du stock disponible.");
          return;
        }
      }
      // In update mode, require a pack with stock OR the currently associated pack
      if (currentStep === "Pack" && action === "update") {
        if (loadingPacks) {
          notifyError("Veuillez attendre le chargement des packs.");
          return;
        }
        if (!selectedPack) {
          notifyError("Veuillez sélectionner un pack.");
          return;
        }
        const hasStock = selectedPack.quotas?.agents?.remaining > 0;
        const isCurrentPack = selectedPack.id === currentPackId;
        if (!hasStock && !isCurrentPack) {
          notifyError("Veuillez sélectionner un pack avec du stock disponible ou le pack actuellement associé.");
          return;
        }
      }

      // Execute step-specific logic before navigating forward
      let canProceed = true;
      if (currentStep === "Adexgénie") {
        // Ensure agentName is a string before sending it to the backend
        console.log("[DEBUG] Checking agent name:", agentName, "type:", typeof agentName);
        console.log("[DEBUG] Selected language:", language);
        
        // Validate that agent name is not empty
        if (!agentName || (typeof agentName === 'string' && agentName.trim() === '')) {
          notifyError("Le nom de l'agent est obligatoire.");
          setIsNextLoading(false);
          return false;
        }
        
        console.log("[DEBUG] Agent name validation passed, sending:", agentName);
        
        canProceed = await handleNextStep("Adexgénie", { 
          agentType, 
          agentName: agentName,
          systemPrompt: "",
          language: language,
          gender: gender,
          aiAgentType: aiAgentType,
          platformMode: platformMode,
          billingCycle: billingCycle,
          currency: currency,
          temperature: temperature,
          maxTokens: maxTokens,
          brandInfo: {
            brand_name: brandName,
            brand_description: brandDescription,
            brand_website: brandWebsite,
            business_hours: businessHours,
            brand_contact_email: brandContactEmail,
            brand_contact_phone: brandContactPhone,
          },
        });
      } else if (currentStep === "Services") {
        if (agentType && !serviceAgentType) {
          notifyError("Please select a service agent type.");
          return;
        }
        canProceed = await handleNextStep("Services", {
          serviceAgentType,
          services,
          business_type_id: servicetType?.id || agentType?.id,
        });
      } else if (currentStep === "Phone Number") {
        if (aiAgentType === 'voice' && !twilioProvisionedNumber) {
          notifyError("Please acquire a phone number for your voice agent.");
          return;
        }
        canProceed = true;
      } else if (currentStep === "Files") {
        // KB and WF are optional — user can proceed without selecting any
        // But if files are selected and upload is in progress, block
        const fileStatusValues = Object.values(fileStatuses);
        const hasUploadingFiles = fileskn.length > 0 && fileStatusValues.length === 0;
        const hasPendingFiles = fileStatusValues.some(
          (s) => s.status && !['success', 'completed', 'error', 'failed'].includes(s.status)
        );
        const hasFailedFiles = fileStatusValues.some(
          (s) => s.status === 'error' || s.status === 'failed'
        );

        if (hasUploadingFiles) {
          notifyError('Files are selected but not uploaded yet. Click "Add to Knowledge Base" to upload them, or remove them to proceed without files.');
          return;
        }
        if (hasPendingFiles) {
          notifyError('Some files are still being processed. Please wait for them to complete, or proceed after removing them.');
          return;
        }
        if (hasFailedFiles) {
          notifyError('Some files failed to process. Please remove failed files or retry upload before proceeding.');
          return;
        }

        // Warn (but allow proceeding) if a selected KB has no adexcloud_kb_id
        const unsyncedKbs = wizardKbs.filter((kb) => !kb.adexcloud_kb_id);
        if (unsyncedKbs.length > 0) {
          console.warn('Warning: KBs without adexcloud_kb_id will not be synced:', unsyncedKbs.map(k => k.name));
        }
        // Warn (but allow proceeding) if a selected WF has no adexcloud_workflow_id
        const unsyncedWfs = wizardWorkflows.filter((wf) => !wf.adexcloud_workflow_id);
        if (unsyncedWfs.length > 0) {
          console.warn('Warning: Workflows without adexcloud_workflow_id will not be synced:', unsyncedWfs.map(w => w.name));
        }

        // Envoyer les IDs des fichiers et les informations de statut au backend
        const filesData = Object.entries(fileStatuses).map(([name, status]) => ({
          name,
          id: status.id,
          status: status.status
        }));
        
        canProceed = await handleNextStep("Files", {
          files: filesData,
          fileIds: fileidx,
        });
      } else if (currentStep === "Deployability Check") {
        if (!selectedPack) {
          notifyError('Please select a pack before deploying the agent.');
          return;
        }
        // Real pre-flight quota check before creating the agent
        const accessToken = user?.access_token || user?.token || localStorage.getItem("access_token");
        if (!accessToken) {
          notifyError('Authentication error. Please log in again.');
          return;
        }
        try {
          let quotaUrl = `/api/billing/quota/?company_pack_id=${selectedPack.id}`;
          if (action === 'update' && currentAgentId) {
            quotaUrl += `&agent_id=${currentAgentId}`;
          }
          const quotaRes = await axios.get(
            quotaUrl,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          const quotaData = quotaRes.data?.data;
          setDeployabilityResult({
            deployable: !!quotaData?.can_create,
            message: quotaData?.message || (quotaData?.can_create ? 'All quotas are sufficient.' : 'Pack quota exceeded.'),
            details: quotaData?.details || null
          });
          if (!quotaData?.can_create) {
            notifyError(
              `${quotaData?.message || 'Pack quota exceeded.'} Go to Billing to purchase more quota.`
            );
            return;
          }
        } catch (quotaErr) {
          console.error('Quota check error:', quotaErr);
          setDeployabilityResult({ deployable: false, message: 'Unable to verify pack quota. Please try again.' });
          notifyError('Unable to verify pack quota. Please try again.');
          return;
        }
        const deploymentData = {
          agentConfig,
          fileidx,
          business_type_id: servicetType?.id || agentType?.id,
          company_pack_id: selectedPack.id,
        };
        if (twilioProvisionedNumber) {
          deploymentData.phone_number = twilioProvisionedNumber;
        }
        // Always send KB and WF IDs in update mode (even if empty) so backend can unassign removed ones
        if (action === "update" || wizardWorkflows.length > 0) {
          deploymentData.workflow_ids = wizardWorkflows.map((w) => w.id).filter(Boolean);
          if (wizardWorkflows.length > 0) {
            deploymentData.workflows = wizardWorkflows;
          }
        }
        if (action === "update" || wizardKbs.length > 0) {
          deploymentData.kb_ids = wizardKbs.map((kb) => kb.id).filter(Boolean);
          deploymentData.kb_id = wizardKb?.id || null;
          if (wizardKbs.length > 0) {
            deploymentData.knowledge_base = wizardKb;
            deploymentData.knowledge_bases = wizardKbs;
          }
        }
        canProceed = await handleNextStep("Agent Deployment", deploymentData);
      } else if (currentStep === "Congratulations!") {
        if (action === "update") {
          // In update mode, navigate back to the agent list
          navigate('/agents');
        } else {
          // In creation mode, reset the wizard to create a new agent
          setActiveStepIndex(0);
          setAgentName("");
          setBrandName("");
          setBrandDescription("");
          setBrandWebsite("");
          setBusinessHours("");
          setBrandContactEmail("");
          setBrandContactPhone("");
          setTwilioProvisionedNumber(null);
          setAgentExternalId(null);
          setWidgetCode(null);
          setCallStatus("idle");
          setFileskn([]);
          setFileStatuses({});
          setFileidx([]);
          setSelectedPack(null);
          setStepStatus({
            "Pack": "pending",
            "Adexgénie": "pending",
            "Services": "pending",
            "Phone Number": "pending",
            "Files": "pending",
            "Deployability Check": "pending",
          });
        }
        return; // Stop further processing
      }
      
      // Move to the requested step only if the current step was successful
      if (canProceed) {
        setActiveStepIndex(requestedStepIndex);
      }
    },
    [steps, activeStepIndex, handleNextStep, agentType, agentName, brandName, brandDescription, brandWebsite, businessHours, brandContactEmail, brandContactPhone, serviceAgentType, services, agentConfig, fileidx, servicetType?.id, fileStatuses, fileskn, action, language, gender, notifyError, navigate, selectedPack, availablePacks, loadingPacks, currentPackId, user, aiAgentType, billingCycle, currency, maxTokens, platformMode, temperature, twilioProvisionedNumber, wizardKb, wizardKbs, wizardWorkflows]
  );

  // Ajouter un useEffect pour gérer la sélection des cartes de service en mode mise à jour
  useEffect(() => {
    // Only run in update mode when serviceAgentTypes are loaded
    if (action === "update" && serviceAgentTypes.length > 0 && window.servicesDataFromBackend) {
      console.log("[DEBUG] Service agent types loaded in update mode, trying to match with backend data");
      const servicesData = window.servicesDataFromBackend;
      
      if (servicesData.serviceAgentType) {
        const serviceTypeId = typeof servicesData.serviceAgentType === 'string' 
          ? servicesData.serviceAgentType 
          : servicesData.serviceAgentType.id || servicesData.serviceAgentType.value_identifier;
        
        console.log("[DEBUG] Looking for service type with ID:", serviceTypeId);
        
        const matchedType = serviceAgentTypes.find(type => 
          type.id === serviceTypeId || 
          type.value_identifier === serviceTypeId
        );
        
        if (matchedType) {
          console.log("[DEBUG] Found matching service agent type:", matchedType);
          setServiceAgentType(matchedType);
        } else {
          console.log("[DEBUG] No matching service agent type found");
        }
      }
    }
  }, [serviceAgentTypes, action]);

  const fileUploadAccessibilityFix = `
    /* Override to fix FileUpload accessibility issues */
    .awsui_upload-button_4xu1k_1u2yz_181[aria-hidden="true"],
    .awsui-upload-button-wrapper button[aria-hidden="true"],
    button[aria-hidden="true"] {
      aria-hidden: false !important;
    }
    
    /* Ensure proper focus visibility */
    .awsui_upload-button_4xu1k_1u2yz_181:focus,
    .awsui-upload-button-wrapper button:focus,
    .awsui-upload-button-wrapper button:focus-visible {
      outline: 2px solid #0972d3 !important;
      outline-offset: 2px !important;
    }
  `;

  return (
    <AppLayoutWrapper
      disableContentPaddings
      toolsHide={false}
      splitPanelPreferences={{ position: isSplitPanelSide }}
      splitPanelSize={isSplitPanelSize}
      splitPanelOpen={isSplitPanelOpen}
      onSplitPanelToggle={() => setIsSplitPanelOpen(!isSplitPanelOpen)}
      onSplitPanelPreferencesChange={({ detail }) =>
        setIsSplitPanelSide(detail.position)
      }
      onSplitPanelResize={({ detail }) => setIsSplitPanelSize(detail.size)}
      toolsOpen={isToolsOpen}
      onToolsChange={({ detail }) => setIsToolsOpen(detail.open)}
      tools={
        <HelpPanel>
          <SpaceBetween size="m">
            <Header variant="h2">Deployment Agent Mi-Genie Guide</Header>
            <Box variant="p">
              <p>
                Enjoy a user-friendly and interactive tool that guides you
                step-by-step to create and deploy an intelligent conversational
                agent (AI) tailored to your needs. Whether you want to set up a
                virtual receptionist, technical support, or a specialized
                assistant, this wizard will accompany you throughout the
                process.
              </p>
            </Box>

            <Header variant="h3">Step-by-Step Configuration</Header>

            <ExpandableSection header="Agent Creation">
              <SpaceBetween size="s">
                <p>
                  <strong>Agent Name:</strong> Give your agent a name (e.g., "My
                  Receptionist AI") to personalize it.
                </p>
                <p>
                  <strong>Agent Type:</strong> Choose from different types of
                  agents, such as a receptionist to manage appointments and
                  calls, or technical support to answer questions, resolve
                  issues, and automatically handle tickets.
                </p>
              </SpaceBetween>
            </ExpandableSection>

            <ExpandableSection header="Service Selection">
              <SpaceBetween size="s">
                <p>
                  <strong>Predefined Services:</strong> Choose from predefined
                  services like plumbing, mechanics, or carpentry.
                </p>
                <p>
                  <strong>Custom Services:</strong> Add your own services if
                  needed (e.g., "Maintenance" or "Installation").
                </p>
              </SpaceBetween>
            </ExpandableSection>

            <ExpandableSection header="Phone Number Assignment">
              <p>
                A dedicated phone number is automatically assigned to your agent
                so your customers can call it.
              </p>
            </ExpandableSection>

            <ExpandableSection header="Agent Deployment">
              <p>
                Once all steps are validated, your agent is ready to be
                deployed. You will receive a dedicated phone number and can
                start using your AI agent immediately.
              </p>
            </ExpandableSection>

            <ExpandableSection header="Agent Testing">
              <SpaceBetween size="s">
                <p>
                  <strong>Test Call:</strong> Test your agent by initiating a
                  voice call directly from the interface. A visual soundwave
                  shows the intensity of the conversation in real time.
                </p>
                <p>
                  <strong>Visual Avatar (Optional):</strong> If enabled, a 3D
                  animated avatar accompanies the agent during the call, adding
                  an immersive touch.
                </p>
              </SpaceBetween>
            </ExpandableSection>

            <Header variant="h3">User Interface</Header>
            <Box variant="p">
              <p>
                Follow simple steps to configure your agent. Each step is
                clearly explained and accompanied by easy-to-fill forms.
              </p>
              <p>
                <strong>Navigation Bar:</strong> Quickly access different
                sections of the application, such as agent management,
                statistics, and files.
              </p>
              <p>
                <strong>Steps Status Panel:</strong> Check the status of each
                step (successful, pending, or failed) in a convenient help
                panel.
              </p>
            </Box>

            <Header variant="h3">Why Choose Adexgénie?</Header>
            <SpaceBetween size="s">
              <Box variant="p">
                <p>
                  <strong>Customization:</strong> Create an AI agent that
                  perfectly matches your needs and your business.
                </p>
                <p>
                  <strong>Simplicity:</strong> An intuitive interface guides you
                  without requiring technical skills.
                </p>
                <p>
                  <strong>Efficiency:</strong> Quickly deploy an agent ready to
                  interact with your clients, 24/7.
                </p>
                <p>
                  <strong>Immersive Experience:</strong> Test your agent with a
                  visual avatar and interactive soundwaves.
                </p>
              </Box>
            </SpaceBetween>
            <Box variant="p">
              <p>
                <strong>Adexgénie</strong> is the ultimate tool for those who
                want to integrate conversational AI into their operations,
                whether for call management, customer support, or appointment
                scheduling. Follow the steps, customize your agent, and watch it
                come to life in just a few clicks! 🚀
              </p>
            </Box>
          </SpaceBetween>
          <br></br>
          <br></br>
        </HelpPanel>
      }
      content={
        <div>
          <style>{fileUploadAccessibilityFix}</style>
          <ContentLayout
            breadcrumbs={
              <BreadcrumbGroup
                items={[
                  { text: "Home", href: "/dashboard" },
                  { text: "Agents", href: "/agents" },
                  { text: action === "update" ? "Update Agent" : "Create Agent", href: "#" },
                ]}
                onFollow={(event) => {
                  event.preventDefault();
                  if (event.detail.href !== '#') navigate(event.detail.href);
                }}
              />
            }
            defaultPadding
            headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
            header={
              <Header
                variant="h1"
                info={
                  <Link variant="info" onFollow={() => setIsToolsOpen(true)}>
                    Info
                  </Link>
                }
                description={action === "update" ? "Update your existing Adexgénie AI Agent" : "Create your Adexgénie AI Agent"}
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button iconName="close" onClick={() => navigate('/agents')}>
                      Close
                    </Button>
                  </SpaceBetween>
                }
              >
                {action === "update" ? "Update Agent" : "Create Agent"}
                {action === "update" && creationId && (
                  <Alert type="info">
                    You are currently updating an existing agent (ID: {creationId}). All fields are pre-populated with the current agent data.
                  </Alert>
                )}
              </Header>
            }
          >
            <br></br> <br></br>
            <Container
              padding={{ top: "xl", bottom: "l" }}
            >
              <Wizard
                steps={steps}
                activeStepIndex={activeStepIndex}
                onNavigate={isNextLoading ? undefined : handleNavigate}
                submitButtonText="Add Another Agent"
                i18nStrings={{
                  stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
                  collapsedStepsLabel: (stepNumber, stepsCount) =>
                    `Step ${stepNumber} of ${stepsCount}`,
                  skipToButtonLabel: (step) => `Skip to ${step.title}`,
                  navigationAriaLabel: "Steps",
                  cancelButton: (activeStepIndex === steps.length - 1 && stepStatus["Deployability Check"] === "success") || 
                               (stepStatus["Phone Number"] === "success") ? 
                               undefined : "Cancel",
                  previousButton: stepStatus["Deployability Check"] === "success" ? undefined : "Previous",
                  nextButton: activeStepIndex === steps.length - 1 ? 
                              (action === "update" ? "Return to List" : "Add New Agent") : 
                              (isNextLoading ? <Spinner /> : "Next"),
                  disabled: isNextLoading || (activeStepIndex === 0 && (loadingPacks || !selectedPack || (action !== 'update' && availablePacks.filter(p => p.quotas?.agents?.remaining > 0).length === 0) || (action === 'update' && !(selectedPack?.quotas?.agents?.remaining > 0 || selectedPack?.id === currentPackId)))),
                  submitButton: undefined,
                }}
                secondaryActions={
                  steps[activeStepIndex]?.hideButtons 
                    ? <div></div> 
                    : undefined
                }
                primaryActions={
                  steps[activeStepIndex]?.hideButtons 
                    ? <div></div> 
                    : undefined
                }
                onCancel={() => {
                  // Reset wizard state for a new agent
                  setActiveStepIndex(0);
                  setAgentName("");
                  setBrandName("");
                  setBrandDescription("");
                  setBrandWebsite("");
                  setBusinessHours("");
                  setBrandContactEmail("");
                  setBrandContactPhone("");
                  setTwilioProvisionedNumber(null);
                  setAgentExternalId(null);
                  setWidgetCode(null);
                  setCallStatus("idle");
                  setFileskn([]);
                  setFileStatuses({});
                  setFileidx([]);
                  setSelectedPack(null);
                  setStepStatus({
                    "Pack": "pending",
                    "Adexgénie": "pending",
                    "Services": "pending",
                    "Phone Number": "pending",
                    "Files": "pending",
                    "Deployability Check": "pending",
                  });
                  setServices([]);
                }}
              />
            </Container>
          </ContentLayout>
        </div>
      }
      splitPanel={
        <SplitPanel
          header="Steps Status"
          key="split-panel"
          data-scrolled="blue"
        >
          <SpaceBetween size="m">
            {steps.map((step, index) => {
              const status = stepStatus[step.title] || "pending";
              return (
                <StatusIndicator
                  key={index}
                  type={
                    status === "success"
                      ? "success"
                      : status === "failed"
                      ? "error"
                      : "pending"
                  }
                >
                  {step.title} -{" "}
                  {status === "success"
                    ? "Success"
                    : status === "failed"
                    ? "Failed"
                    : "Pending"}
                  {step.title === "Twilio Number" && status === "success" && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      <Icon name="phone" /> {/* Phone icon */}
                      <span>{twilioProvisionedNumber}</span>{" "}
                      {/* Twilio-provisioned phone number */}
                    </div>
                  )}
                </StatusIndicator>
              );
            })}
          </SpaceBetween>
        </SplitPanel>
      }
    />
  );
};

export default AssistantWizard;
